import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import type { ElementType } from "react";
import {
  ChevronRight,
  X,
  Activity,
  TrendingDown,
  Clock,
  Loader2,
  CheckCircle2,
  XCircle,
  Search,
  ArrowLeft,
  Copy,
  Check,
  GitBranch,
  AlertTriangle,
  MousePointerClick,
  BarChart3,
  LineChart,
  CalendarDays,
} from "lucide-react";
import { DataTable, ViewModeToggle, type Column } from "../../components/DataTable";
import { Tooltip, TooltipTrigger, TooltipContent } from "../../components/ui/tooltip";
import { Modal, ModalShell } from "../../components/Modal";
import { PageShell } from "../../components/layout/PageShell";
import { Dropdown } from "../../components/Dropdown";
import {
  getPageSizeOptions,
  resolvePageSizeForViewMode,
} from "../../../utils/formatters";
import { StatusBadge } from "../../components/StatusBadge";
import {
  MetaDates,
  StatTile as CardStatTile,
  StatTiles as CardStatTiles,
  TitleTooltip,
  TypeBadge,
  statusAccent,
} from "../../components/EntityCardGrid";
import { Globe, FileText, ShieldCheck } from "lucide-react";
import {
  listSSPs,
  getAssessmentPlan,
  getAssessmentPlanReviewedControls,
  postDrift,
  postDriftBySsp,
  postDriftByPlan,
  fetchAssessmentPlansBySsp,
  getDriftEvidence,
  type ApiAssessmentPlan,
  type ApiReviewedControl,
  type SspSummaryResponse,
  type DriftApiControl,
  type DriftHistoryPoint,
  type DriftOpaDetail,
  type DriftSummary,
} from "../../../api/frameworksApi";

// --- Utilities ---

function fmtDate(iso: string): string {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function fmtDay(iso: string): string {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return `${d.toLocaleString("en-US", { month: "short" })} ${String(
      d.getUTCDate(),
    ).padStart(2, "0")}`;
  } catch {
    return iso;
  }
}

function toStartIso(ymd: string): string {
  return `${ymd}T00:00:00Z`;
}
function toEndIso(ymd: string): string {
  return `${ymd}T23:59:59Z`;
}

function planDates(plan: ApiAssessmentPlan): { start: string; end: string } {
  const rawStart = plan.task?.startDate || plan.startDate;
  const rawEnd = plan.task?.endDate || plan.endDate;
  if (rawStart && rawEnd) {
    return { start: rawStart.slice(0, 10), end: rawEnd.slice(0, 10) };
  }
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - 30);
  return {
    start: start.toISOString().slice(0, 10),
    end: end.toISOString().slice(0, 10),
  };
}

// --- Status badges ---

const PLAN_STATUS: Record<string, { label: string; cls: string }> = {
  draft: { label: "Draft", cls: "bg-gray-500/20 text-gray-400" },
  in_review: { label: "In Review", cls: "bg-yellow-500/20 text-yellow-400" },
  approved: { label: "Approved", cls: "bg-green-500/20 text-green-400" },
  rejected: { label: "Rejected", cls: "bg-red-500/20 text-red-400" },
};

const CTRL_STATUS: Record<string, { label: string; cls: string }> = {
  passed: { label: "Passed", cls: "bg-green-500/20 text-green-400" },
  failed: { label: "Failed", cls: "bg-red-500/20 text-red-400" },
  unknown: { label: "Unknown", cls: "bg-gray-500/20 text-gray-400" },
};

function PlanStatusBadge({ status }: { status?: string }) {
  const key = (status || "").toLowerCase().replace(/[-\s]/g, "_");
  const label = PLAN_STATUS[key]?.label ?? status ?? "-";
  return <StatusBadge status={key as any} label={label} />;
}

function CtrlStatusBadge({ status }: { status?: string }) {
  const key = (status || "").toLowerCase();
  const s = CTRL_STATUS[key] ?? {
    label: status || "-",
    cls: "bg-gray-500/20 text-gray-400",
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.cls}`}>
      {s.label}
    </span>
  );
}

// --- KPI Card ---

function KpiCard({
  label,
  value,
  icon: Icon,
  colorCls,
}: {
  label: string;
  value: string | number;
  icon: ElementType;
  colorCls: string;
}) {
  const isLongText = typeof value === "string" && value.length > 4;
  return (
    <div className="bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl p-4 flex items-center gap-4 h-[72px] shadow-sm dark:shadow-none">
      <div className={`p-2 rounded-lg shrink-0 ${colorCls}`}>
        <Icon size={20} />
      </div>
      <div className="min-w-0">
        <p
          className={`font-bold text-gray-900 dark:text-white leading-tight ${isLongText ? "text-[15px]" : "text-2xl"}`}
        >
          {value}
        </p>
        <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5 leading-tight">
          {label}
        </p>
      </div>
    </div>
  );
}

// --- Evidence Modal ---

interface EvidenceItem {
  command?: string;
  data?: string;
}

/** Parse the normalized evidence string into discrete {command, data} items.
 *  Falls back to a single raw block when the payload isn't the expected shape. */
function parseEvidenceItems(raw?: string): {
  items: EvidenceItem[];
  fallback: string | null;
} {
  if (!raw) return { items: [], fallback: null };
  try {
    const p = JSON.parse(raw);
    if (Array.isArray(p)) {
      const items = p.filter(
        (x): x is EvidenceItem => !!x && typeof x === "object",
      );
      return { items, fallback: items.length ? null : raw };
    }
    if (p && typeof p === "object") return { items: [p as EvidenceItem], fallback: null };
    return { items: [], fallback: raw };
  } catch {
    return { items: [], fallback: raw };
  }
}

/** Count of evidence items, for badges. */
function evidenceCount(raw?: string): number | null {
  if (!raw) return null;
  const { items, fallback } = parseEvidenceItems(raw);
  if (items.length) return items.length;
  return fallback ? 1 : null;
}

/** Pretty-print data as JSON if it parses, else return it verbatim. */
function formatEvidenceData(data?: string): string {
  if (data == null || data === "") return "";
  try {
    return JSON.stringify(JSON.parse(data), null, 2);
  } catch {
    return data;
  }
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard?.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      }}
      className="flex items-center gap-1 text-[13px] font-semibold text-foreground/85 hover:text-foreground transition-colors"
      title="Copy"
    >
      {copied ? (
        <>
          <Check size={12} className="text-green-500" /> Copied
        </>
      ) : (
        <>
          <Copy size={12} /> Copy
        </>
      )}
    </button>
  );
}

function EvidenceModal({
  detail,
  onClose,
  onBack,
}: {
  detail: DriftOpaDetail;
  onClose: () => void;
  onBack?: () => void;
}) {
  const { items, fallback } = parseEvidenceItems(detail.evidence);
  const count = items.length || (fallback ? 1 : 0);
  const isSatisfied =
    detail.status.toLowerCase() === "passed" ||
    detail.status.toLowerCase() === "satisfied";

  return (
    <ModalShell open onClose={onClose} variant="wide">
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 p-5 border-b border-border flex-shrink-0">
          {/* Left: back - divider - title + asset */}
          <div className="flex items-center gap-3 min-w-0">
            {onBack && (
              <>
                <button
                  onClick={onBack}
                  className="flex-shrink-0 flex items-center gap-1.5 pl-1.5 pr-2.5 py-1.5 rounded-lg text-sm font-medium text-foreground/80 hover:text-foreground hover:bg-accent transition-colors"
                >
                  <ChevronRight size={16} className="rotate-180" />
                  Back
                </button>
                <span className="flex-shrink-0 h-8 w-px bg-border" />
              </>
            )}
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-foreground leading-tight">
                Evidence
              </h2>
              <p className="text-[13px] d mt-0.5 truncate">
                Asset:{" "}
                <span className="text-foreground/80 font-medium">
                  {detail.assetName || detail.assetUuid}
                </span>
              </p>
            </div>
          </div>

          {/* Right: status pill - close */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <span
              className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                isSatisfied
                  ? "bg-green-500/15 text-green-700 dark:text-green-400 border border-green-500/30"
                  : "bg-red-500/15 text-red-700 dark:text-red-400 border border-red-500/30"
              }`}
            >
              <span>{isSatisfied ? "" : ""}</span>
              {isSatisfied ? "Satisfied" : "Not Satisfied"}
            </span>
            <button
              onClick={onClose}
              className="p-1.5 -mr-1.5 rounded-md d hover:text-foreground hover:bg-accent transition-colors"
              aria-label="Close"
            >
              <X className="size-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto evidence-scrollbar p-5">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-base font-semibold text-foreground">
              Evidence{count > 0 ? ` (${count})` : ""}
            </h4>
            {count > 0 && (
              <span className="text-sm text-foreground/75">
                {count} item{count !== 1 ? "s" : ""}
              </span>
            )}
          </div>

          {items.length > 0 ? (
            <div className="flex flex-col gap-4">
              {items.map((it, i) => {
                const output = formatEvidenceData(it.data);
                return (
                  <div
                    key={i}
                    className="min-w-0 rounded-lg border border-border bg-muted/30 dark:bg-[#090b14] flex flex-col overflow-hidden"
                  >
                    {it.command != null && it.command !== "" && (
                      <div className="border-b border-border">
                        <div className="flex items-center justify-between px-4 py-2 bg-muted/40 dark:bg-white/[0.02]">
                          <span className="text-[13px] font-semibold tracking-wide text-foreground/80">
                            Command
                          </span>
                          <CopyButton text={it.command} />
                        </div>
                        <pre className="evidence-scrollbar max-h-[28vh] overflow-x-hidden overflow-y-auto px-4 py-3 text-[15px] font-mono font-medium text-foreground whitespace-pre-wrap break-all leading-7">
                          {it.command}
                        </pre>
                      </div>
                    )}
                    <div>
                      <div className="flex items-center justify-between px-4 py-2 bg-muted/40 dark:bg-white/[0.02] border-b border-border">
                        <span className="text-[13px] font-semibold tracking-wide text-foreground/80">
                          Output
                        </span>
                        {output !== "" && <CopyButton text={output} />}
                      </div>
                      <pre className="evidence-scrollbar min-h-[160px] max-h-[58vh] overflow-x-hidden overflow-y-auto px-4 py-3 text-[15px] font-mono font-medium text-foreground whitespace-pre-wrap break-all leading-7">
                        {output !== "" ? (
                          output
                        ) : (
                          <span className="text-foreground/70">No output.</span>
                        )}
                      </pre>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="min-w-0 rounded-lg border border-border bg-muted/30 dark:bg-[#090b14] flex flex-col overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-muted/40 dark:bg-white/[0.02]">
                <span className="text-[13px] font-semibold tracking-wide text-foreground/80">
                  Evidence
                </span>
                {fallback && <CopyButton text={fallback} />}
              </div>
              <pre className="evidence-scrollbar min-h-[160px] max-h-[64vh] overflow-x-hidden overflow-y-auto p-4 text-[15px] font-mono font-medium text-foreground whitespace-pre-wrap break-all leading-7">
                {fallback ?? "No evidence data."}
              </pre>
            </div>
          )}
        </div>
      </div>
    </ModalShell>
  );
}

// --- OPA Details Modal ---

function isDetailSatisfied(status: string): boolean {
  const s = status.toLowerCase();
  return s === "passed" || s === "satisfied" || s === "pass";
}

function formatDetailStatus(status: string): string {
  const s = status.toLowerCase();
  if (s === "passed" || s === "satisfied" || s === "pass") return "Passed";
  if (s === "failed" || s === "not satisfied" || s === "fail") return "Failed";
  return status;
}

type DetailFilter = "all" | "satisfied" | "failed";

function OpaDetailsModal({
  point,
  onClose,
}: {
  point: ScanPoint;
  onClose: () => void;
}) {
  const [evidenceDetail, setEvidenceDetail] = useState<DriftOpaDetail | null>(
    null,
  );
  const [filter, setFilter] = useState<DetailFilter>("all");
  const [query, setQuery] = useState("");

  const details = point.raw.opaDetails ?? [];
  const hh = Math.floor(point.x / 60)
    .toString()
    .padStart(2, "0");
  const mm = (point.x % 60).toString().padStart(2, "0");

  const passedCount = details.filter((d) => isDetailSatisfied(d.status)).length;
  const failedCount = details.length - passedCount;
  const passPercent =
    details.length > 0 ? Math.round((passedCount / details.length) * 100) : 0;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return details.filter((od) => {
      const sat = isDetailSatisfied(od.status);
      if (filter === "satisfied" && !sat) return false;
      if (filter === "failed" && sat) return false;
      if (!q) return true;
      const hay = `${od.assetName ?? ""} ${od.assetUuid ?? ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [details, filter, query]);

  const tabs: { key: DetailFilter; label: string; count: number }[] = [
    { key: "all", label: "All", count: details.length },
    { key: "satisfied", label: "Satisfied", count: passedCount },
    { key: "failed", label: "Not Satisfied", count: failedCount },
  ];

  return (
    <>
      <Modal
        open
        onClose={onClose}
        title="Automated Check Results"
        description={`${point.dayLabel} - ${hh}:${mm} UTC`}
      >
        {/* Compliance summary */}
        <div className="rounded-xl border border-border bg-gradient-to-br from-muted/30 to-transparent dark:from-white/[0.03] p-4">
          <div className="flex items-end justify-between gap-4">
            <div>
              <div className="flex items-baseline gap-1.5">
                <span
                  className={`text-3xl font-bold leading-none ${
                    passPercent === 100
                      ? "text-emerald-500"
                      : passPercent === 0
                        ? "text-red-500"
                        : "text-foreground"
                  }`}
                >
                  {passPercent}%
                </span>
                <span className="text-xs d">compliant</span>
              </div>
              <p className="text-[11px] d mt-1">
                {details.length} automated check
                {details.length !== 1 ? "s" : ""} at this scan
              </p>
            </div>
            <div className="flex items-center gap-5">
              <div className="text-right">
                <p className="text-xl font-bold text-emerald-500 leading-none">
                  {passedCount}
                </p>
                <p className="text-[10px] d mt-1 tracking-wide">
                  Satisfied
                </p>
              </div>
              <div className="text-right">
                <p className="text-xl font-bold text-red-500 leading-none">
                  {failedCount}
                </p>
                <p className="text-[10px] d mt-1 tracking-wide">
                  Failed
                </p>
              </div>
            </div>
          </div>
          {details.length > 0 && (
            <div className="mt-3 h-1.5 rounded-full overflow-hidden flex bg-red-500/25">
              <div
                className="bg-emerald-500 transition-all"
                style={{ width: `${passPercent}%` }}
              />
            </div>
          )}
        </div>

        {/* Filter + search toolbar */}
        {details.length > 0 && (
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[160px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 d/60 pointer-events-none" />
              <input
                type="text"
                placeholder="Search asset"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-xs bg-card border border-border rounded-lg outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all placeholder:d/50"
              />
            </div>
            <div className="inline-flex rounded-lg bg-muted/40 dark:bg-white/[0.04] p-0.5">
              {tabs.map((t) => {
                const active = filter === t.key;
                return (
                  <button
                    key={t.key}
                    onClick={() => setFilter(t.key)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                      active
                        ? "bg-card text-foreground shadow-sm"
                        : "d hover:text-foreground"
                    }`}
                  >
                    {t.label}
                    <span
                      className={`px-1.5 py-px rounded-full text-[10px] font-semibold ${
                        active
                          ? t.key === "failed"
                            ? "bg-red-500/15 text-red-500"
                            : t.key === "satisfied"
                              ? "bg-emerald-500/15 text-emerald-500"
                              : "bg-indigo-500/15 text-indigo-500"
                          : "bg-muted/60 dark:bg-white/[0.06] d"
                      }`}
                    >
                      {t.count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Result rows */}
        <div className="space-y-2">
          {details.length === 0 ? (
            <p className="text-sm d px-5 py-10 text-center rounded-xl border border-dashed border-border">
              No automated check results for this scan point.
            </p>
          ) : filtered.length === 0 ? (
            <p className="text-sm d px-5 py-10 text-center rounded-xl border border-dashed border-border">
              No results match your filter.
            </p>
          ) : (
            filtered.map((od, i) => {
              const count = evidenceCount(od.evidence);
              const isSatisfied = isDetailSatisfied(od.status);
              return (
                <div
                  key={i}
                  className={`group flex items-center gap-3 rounded-xl border bg-card px-3.5 py-3 transition-all ${
                    isSatisfied
                      ? "border-border hover:border-emerald-500/40"
                      : "border-red-500/20 hover:border-red-500/50"
                  } hover:shadow-sm`}
                >
                  {/* Status icon */}
                  <div
                    className={`shrink-0 size-9 rounded-lg grid place-items-center ${
                      isSatisfied
                        ? "bg-emerald-500/10 text-emerald-500"
                        : "bg-red-500/10 text-red-500"
                    }`}
                  >
                    {isSatisfied ? (
                      <CheckCircle2 size={18} />
                    ) : (
                      <XCircle size={18} />
                    )}
                  </div>

                  {/* Status + asset (fixed-width status so assets align) */}
                  <div className="min-w-0 flex-1 flex items-center gap-3">
                    <span
                      className={`text-xs font-semibold w-24 shrink-0 ${
                        isSatisfied
                          ? "text-emerald-600 dark:text-emerald-400"
                          : "text-red-600 dark:text-red-400"
                      }`}
                    >
                      {isSatisfied ? "Satisfied" : "Not Satisfied"}
                    </span>
                    <span className="text-xs truncate">
                      <span className="d">Asset: </span>
                      <span className="text-foreground font-medium">
                        {od.assetName || od.assetUuid || "-"}
                      </span>
                    </span>
                  </div>

                  {/* Evidence */}
                  {od.evidence && (
                    <button
                      onClick={() => setEvidenceDetail(od)}
                      className="shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-indigo-500/10 border border-indigo-500/20 text-indigo-600 dark:text-indigo-400 text-[11px] font-medium hover:bg-indigo-500/20 transition-colors"
                    >
                      Evidence{count !== null ? ` (${count})` : ""}
                      <span className="text-[10px]"></span>
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>
      </Modal>

      {evidenceDetail && (
        <EvidenceModal
          detail={evidenceDetail}
          onBack={() => setEvidenceDetail(null)}
          onClose={() => {
            setEvidenceDetail(null);
            onClose();
          }}
        />
      )}
    </>
  );
}

// --- Inline scan details (rendered after the graph when a diamond is clicked) ---

function EvidenceBlock({
  label,
  text,
  tone = "neutral",
}: {
  label: string;
  text: string;
  tone?: "command" | "output" | "neutral";
}) {
  const borderL =
    tone === "command"
      ? "border-l-indigo-500/70"
      : tone === "output"
        ? "border-l-teal-500/70"
        : "border-l-border";

  return (
    <div
      className={`rounded-lg border border-border border-l-[3px] overflow-hidden bg-muted/30 dark:bg-[#090b14] ${borderL}`}
    >
      <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-muted/40 dark:bg-white/[0.02]">
        <span className="text-sm font-semibold tracking-wide text-foreground/85">
          {label}
        </span>
        <CopyButton text={text} />
      </div>
      <pre className="evidence-scrollbar max-h-[44vh] overflow-x-hidden overflow-y-auto px-4 py-3 text-[15px] font-mono font-medium text-foreground whitespace-pre-wrap break-all leading-7">
        {text}
      </pre>
    </div>
  );
}

const STATUS_LINE_RE = /^(\s*"status":\s*")(pass(?:ed)?|fail(?:ed)?)(",?)(\s*)$/i;

function EvidenceBlockWithStatusHighlight({ label, text }: { label: string; text: string }) {
  const lines = text.split("\n");
  return (
    <div className="rounded-lg border border-border border-l-[3px] border-l-teal-500/70 overflow-hidden bg-muted/30 dark:bg-[#090b14]">
      <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-muted/40 dark:bg-white/[0.02]">
        <span className="text-sm font-semibold tracking-wide text-foreground/85">{label}</span>
        <CopyButton text={text} />
      </div>
      <pre className="evidence-scrollbar max-h-[44vh] overflow-x-hidden overflow-y-auto px-4 py-3 text-[15px] font-mono font-medium whitespace-pre-wrap break-all leading-7">
        {lines.map((line, i) => {
          const m = line.match(STATUS_LINE_RE);
          if (m) {
            const [, prefix, val, suffix, trailing] = m;
            const isPass = val.toLowerCase().startsWith("pass");
            return (
              <span key={i} className="block">
                <span className="text-foreground">{prefix}</span>
                <span className={isPass ? "text-emerald-400 font-bold" : "text-red-400 font-bold"}>{val}</span>
                <span className="text-foreground">{suffix}{trailing}</span>
              </span>
            );
          }
          return <span key={i} className="block text-foreground">{line}</span>;
        })}
      </pre>
    </div>
  );
}

function ScanResultCard({ od }: { od: DriftOpaDetail }) {
  const isSatisfied = isDetailSatisfied(od.status);
  const { items, fallback } = parseEvidenceItems(od.evidence);
  const componentName = getDriftComponentName(od);
  return (
    <div
      className={`rounded-xl border overflow-hidden ${
        isSatisfied ? "border-emerald-500/25" : "border-red-500/30"
      } bg-card`}
    >
      {/* Header: status - control id - component - asset */}
      <div className="flex items-center gap-3 flex-wrap px-4 py-3 border-b border-border bg-muted/20 dark:bg-white/[0.02]">
        <span
          className={`flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
            isSatisfied
              ? "bg-green-500/15 text-green-700 dark:text-green-400 border border-green-500/30"
              : "bg-red-500/15 text-red-700 dark:text-red-400 border border-red-500/30"
          }`}
        >
          {formatDetailStatus(od.status)}
        </span>
        {od.opaControlId && (
          <span className="text-sm text-foreground/95">
            <span className="text-foreground/80 font-medium">Control ID: </span>
            <span className="font-bold text-foreground">{od.opaControlId?.toUpperCase()}</span>
          </span>
        )}
        {componentName && (
          <span className="text-sm text-foreground/95">
            <span className="text-foreground/80 font-medium">Component: </span>
            <span className="font-bold text-foreground">{componentName}</span>
          </span>
        )}
        <span className="text-sm text-foreground/90">
          <span className="text-foreground/80 font-medium">Asset: </span>
          <span className="font-bold text-foreground">
            {od.assetName || od.assetUuid || "-"}
          </span>
        </span>
      </div>

      {/* Control title */}
      {od.controlTitle && (
        <div className="px-4 py-2.5 border-b border-border">
          <span className="text-foreground/80 font-medium text-sm mr-1">Control Title: </span>
          <span className="font-bold text-foreground text-sm">{od.controlTitle}</span>
        </div>
      )}

      {/* Command + Output */}
      <div className="p-4 space-y-3">
        {items.length > 0 ? (
          items.map((it, i) => (
            <div key={i} className="space-y-3">
              {it.command != null && it.command !== "" && (
                <EvidenceBlock label="Command" text={it.command} tone="command" />
              )}
              <EvidenceBlockWithStatusHighlight label="Output" text={formatEvidenceData(it.data) || "No output."} />
            </div>
          ))
        ) : (
          <EvidenceBlock label="Evidence" text={fallback ?? "No evidence data."} />
        )}
      </div>
    </div>
  );
}

function getDriftComponentName(od: DriftOpaDetail): string {
  const raw = od as DriftOpaDetail & {
    componentName?: string | null;
    component_name?: string | null;
    componentTitle?: string | null;
    component_title?: string | null;
    component?: string | { title?: string | null; name?: string | null } | null;
  };
  const value =
    raw.componentName ??
    raw.component_name ??
    raw.componentTitle ??
    raw.component_title ??
    (typeof raw.component === "string"
      ? raw.component
      : raw.component?.title ?? raw.component?.name);
  const explicit = value?.trim();
  if (explicit) return explicit;
  const prefix = od.opaControlId?.split("-")[0]?.toUpperCase();
  if (prefix === "LIN") return "Linux";
  if (prefix === "OCI") return "OCI";
  return prefix || "";
}

const SCAN_BATCH_WINDOW_MS = 5 * 60 * 1000;

function scanTimeMs(point: DriftHistoryPoint): number {
  const ms = new Date(point.timestamp).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

function ScanDetailsList({
  point,
  onClose,
}: {
  point: ScanPoint;
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "passed" | "failed">(
    "all",
  );
  const [componentFilter, setComponentFilter] = useState("");
  // Evidence is fetched lazily from GET /api/v1/drift/evidence per asset on open;
  // the embedded opaDetails (if any) are used as an instant fallback.
  const [details, setDetails] = useState<DriftOpaDetail[]>(
    point.raw.opaDetails ?? [],
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const hh = Math.floor(point.x / 60)
    .toString()
    .padStart(2, "0");
  const mm = (point.x % 60).toString().padStart(2, "0");

  // Bring the report into view so the user sees it open below the timeline.
  useEffect(() => {
    rootRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  useEffect(() => {
    const scanRecords = point.relatedRaw?.length ? point.relatedRaw : [point.raw];
    const embedded = scanRecords.flatMap((record) => record.opaDetails ?? []);
    setDetails(embedded);
    // Group this scan window's tech-controls by asset AND exact timestamp, then
    // make one batched request per asset/timestamp. This preserves backend exact
    // timestamp matching while allowing OCI and Linux records from the same scan
    // window to be loaded together.
    const byAsset = new Map<
      string,
      {
        assetId: string;
        assetName: string;
        timestamp: string;
        controlIds: Set<string>;
        details: DriftOpaDetail[];
      }
    >();
    for (const record of scanRecords) {
      for (const od of record.opaDetails ?? []) {
        if (!od.assetUuid || !od.opaControlId) continue;
        const key = `${od.assetUuid}|${record.timestamp}`;
        let entry = byAsset.get(key);
        if (!entry) {
          entry = {
            assetId: od.assetUuid,
            assetName: od.assetName || od.assetUuid,
            timestamp: record.timestamp,
            controlIds: new Set(),
            details: [],
          };
          byAsset.set(key, entry);
        }
        entry.controlIds.add(od.opaControlId);
        entry.details.push(od);
      }
    }
    const assets = [...byAsset.values()];
    if (assets.length === 0) {
      setDetails(embedded);
      setLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all(
      assets.map((info) =>
        getDriftEvidence(
          {
            assetId: info.assetId,
            timestamp: info.timestamp,
            controlIds: [...info.controlIds],
          },
          info.assetName,
        )
          .then((rows) =>
            rows.length
              ? rows
              : info.details,
          )
          .catch(() => info.details),
      ),
    )
      .then((results) => {
        if (cancelled) return;
        const merged = results.flat();
        // Soft empty state: if nothing came back, fall back to embedded skeleton.
        setDetails(merged.length ? merged : embedded);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(e.message || "Failed to load evidence.");
        setDetails(embedded);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [point.raw, point.relatedRaw]);

  const componentOptions = useMemo(
    () =>
      Array.from(
        new Set(details.map(getDriftComponentName).filter(Boolean)),
      ).sort((a, b) => a.localeCompare(b)),
    [details],
  );

  useEffect(() => {
    if (componentFilter && !componentOptions.includes(componentFilter)) {
      setComponentFilter("");
    }
  }, [componentFilter, componentOptions]);

  const componentScopedDetails = useMemo(
    () =>
      componentFilter
        ? details.filter((od) => getDriftComponentName(od) === componentFilter)
        : details,
    [componentFilter, details],
  );
  const passedCount = componentScopedDetails.filter((d) =>
    isDetailSatisfied(d.status),
  ).length;
  const failedCount = componentScopedDetails.length - passedCount;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return componentScopedDetails.filter((od) => {
      const sat = isDetailSatisfied(od.status);
      if (statusFilter === "passed" && !sat) return false;
      if (statusFilter === "failed" && sat) return false;
      if (!q) return true;
      return `${od.assetName ?? ""} ${od.assetUuid ?? ""} ${getDriftComponentName(od)}`
        .toLowerCase()
        .includes(q);
    });
  }, [componentScopedDetails, query, statusFilter]);

  return (
    <div
      ref={rootRef}
      className="mt-6 scroll-mt-4 rounded-xl border border-indigo-500/30 ring-1 ring-indigo-500/10 bg-muted/10 dark:bg-white/[0.015] overflow-hidden flex flex-col max-h-[75vh]"
    >
      {/* Header (static) */}
      <div className="flex-shrink-0 flex items-center justify-between gap-3 px-4 py-3 border-b border-border bg-muted/30 dark:bg-white/[0.03]">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="text-base font-bold text-foreground">Scan Details</h4>
            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-indigo-500/15 text-indigo-500">
              {details.length}
            </span>
            {loading && (
              <Loader2 className="size-3.5 animate-spin text-indigo-400" />
            )}
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
            Scan: {point.dayLabel} - {hh}:{mm} UTC
          </p>
        </div>
        <button
          onClick={onClose}
          className="flex-shrink-0 p-1.5 rounded-md d hover:text-foreground hover:bg-accent transition-colors"
          aria-label="Close"
        >
          <X className="size-4" />
        </button>
      </div>

      {/* Filter toolbar (static, transparent) */}
      {!loading && details.length > 0 && (
        <div className="flex-shrink-0 flex items-center gap-3 flex-wrap px-4 pt-4">
          <div className="inline-flex rounded-lg bg-muted/40 dark:bg-white/[0.05] p-0.5">
            {(
              [
                { key: "all", label: "All", count: componentScopedDetails.length },
                { key: "passed", label: "Passed", count: passedCount },
                { key: "failed", label: "Failed", count: failedCount },
              ] as const
            ).map((t) => {
              const active = statusFilter === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setStatusFilter(t.key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                    active
                      ? "bg-card text-foreground shadow-sm"
                      : "d hover:text-foreground"
                  }`}
                >
                  {t.label}
                  <span
                    className={`px-1.5 py-px rounded-full text-[10px] font-semibold ${
                      active
                        ? t.key === "failed"
                          ? "bg-red-500/15 text-red-500"
                          : t.key === "passed"
                            ? "bg-emerald-500/15 text-emerald-500"
                            : "bg-indigo-500/15 text-indigo-500"
                        : "bg-muted/60 dark:bg-white/[0.06] d"
                    }`}
                  >
                    {t.count}
                  </span>
                </button>
              );
            })}
          </div>
          <Dropdown
            value={componentFilter}
            onChange={setComponentFilter}
            options={[
              { value: "", label: "All components" },
              ...componentOptions.map((name) => ({ value: name, label: name })),
            ]}
            placeholder="Choose component"
            size="sm"
            align="start"
            className="!h-[36px] !min-w-[160px] !max-w-[210px] text-xs"
          />
          <div className="relative flex-1 min-w-[160px] max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 d/60 pointer-events-none" />
            <input
              type="text"
              placeholder="Filter by asset"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm bg-transparent border border-border rounded-lg outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all placeholder:d/50"
            />
          </div>
        </div>
      )}

      {/* Cards (scrollable) */}
      <div className="flex-1 min-h-0 overflow-y-auto evidence-scrollbar p-4 space-y-3">
        {loading ? (
          <div className="flex flex-col items-center gap-3 py-12 text-sm d">
            <Loader2 className="size-7 animate-spin text-indigo-400" />
            <span className="text-foreground/60">Loading scan data...</span>
          </div>
        ) : details.length === 0 ? (
          <p className="text-sm d px-5 py-10 text-center rounded-xl border border-dashed border-border">
            {error ?? "No automated check results for this scan point."}
          </p>
        ) : filtered.length === 0 ? (
          <p className="text-sm d px-5 py-10 text-center rounded-xl border border-dashed border-border">
            No results match your filter.
          </p>
        ) : (
          <>
            {error && (
              <p className="text-xs text-amber-600 dark:text-amber-400 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
                Showing cached evidence - {error}
              </p>
            )}
            {filtered.map((od, i) => (
              <ScanResultCard key={i} od={od} />
            ))}
          </>
        )}
      </div>
    </div>
  );
}

// --- Compliance Shift Matrix ---

interface ScanPoint {
  x: number;
  y: number;
  status: string;
  raw: DriftHistoryPoint;
  relatedRaw?: DriftHistoryPoint[];
  dayLabel: string;
}

// Full 24-hour span so scans up to 23:59 are visible; last labeled tick is "23",
// and an extra unlabeled grid line marks the end-of-day (24:00) boundary.
const TOTAL_MINUTES = 24 * 60;
const X_TICKS_H = Array.from({ length: 24 }, (_, i) => i); // 0-23
const LEFT_MARGIN = 88; // wider column for day labels
const PLOT_LEFT_PAD = 28; // visible gap between day labels and data area
const RIGHT_MARGIN = 16;
const TOP_MARGIN = 44;
const ROW_HEIGHT = 64;
const BAR_HEIGHT = 32;

interface TooltipState {
  point: ScanPoint;
  mouseX: number;
  mouseY: number;
}

function ComplianceShiftMatrix({
  history,
  highlightStatus,
}: {
  history: DriftHistoryPoint[];
  highlightStatus?: "passed" | "failed" | "drift" | null;
}) {
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const [selectedPoint, setSelectedPoint] = useState<ScanPoint | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(700);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function scheduleHide() {
    hideTimer.current = setTimeout(() => setTooltip(null), 150);
  }
  function cancelHide() {
    if (hideTimer.current) clearTimeout(hideTimer.current);
  }

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setWidth(e.contentRect.width));
    ro.observe(el);
    setWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  const sortedHistory = useMemo(
    () => [...history].sort((a, b) => a.timestamp.localeCompare(b.timestamp)),
    [history],
  );

  // Timestamps involved in a drift event - the point before a status flip (last
  // passed/failed) and the point it flipped to. Used to highlight drift only.
  const driftTimestamps = useMemo(() => {
    const s = new Set<string>();
    for (let i = 1; i < sortedHistory.length; i++) {
      if (sortedHistory[i].status !== sortedHistory[i - 1].status) {
        s.add(sortedHistory[i - 1].timestamp);
        s.add(sortedHistory[i].timestamp);
      }
    }
    return s;
  }, [sortedHistory]);

  const { days, pointsByDay } = useMemo(() => {
    const dayMap = new Map<string, DriftHistoryPoint[]>();
    sortedHistory.forEach((h) => {
      const ymd = h.timestamp.slice(0, 10);
      if (!dayMap.has(ymd)) dayMap.set(ymd, []);
      dayMap.get(ymd)!.push(h);
    });
    const dayList = Array.from(dayMap.keys()).sort();
    return { days: dayList, pointsByDay: dayMap };
  }, [sortedHistory]);

  if (days.length === 0) {
    return (
      <p className="text-sm text-gray-500 text-center py-8">
        No scan data available.
      </p>
    );
  }

  const plotW = width - LEFT_MARGIN - PLOT_LEFT_PAD - RIGHT_MARGIN;
  const plotStartX = LEFT_MARGIN + PLOT_LEFT_PAD;
  const svgH = TOP_MARGIN + days.length * ROW_HEIGHT + 40;

  function minToX(min: number) {
    return plotStartX + (min / TOTAL_MINUTES) * plotW;
  }

  function handleBarMouseEnter(
    e: React.MouseEvent<SVGElement>,
    scanPoint: ScanPoint,
  ) {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    setTooltip({
      point: scanPoint,
      mouseX: e.clientX - rect.left,
      mouseY: e.clientY - rect.top,
    });
  }

  return (
    <div ref={containerRef} className="relative">
      {/* Legend */}
      <div className="flex items-center gap-4 mb-3 text-sm">
        <div className="flex items-center gap-1.5">
          <svg width={13} height={13} viewBox="0 0 12 12">
            <polygon points="6,0 12,6 6,12 0,6" fill="#2ecc71" />
          </svg>
          <span className="font-medium text-gray-700 dark:text-gray-300">
            Pass
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <svg width={13} height={13} viewBox="0 0 12 12">
            <polygon points="6,0 12,6 6,12 0,6" fill="#f4845f" />
          </svg>
          <span className="font-medium text-gray-700 dark:text-gray-300">
            Fail
          </span>
        </div>
      </div>

      <svg
        width={width}
        height={svgH}
        style={{ display: "block" }}
        onMouseLeave={scheduleHide}
        onClick={() => setSelectedPoint(null)}
        className="drift-timeline-svg"
      >
        <style>{`
          .drift-tick-label { fill: #64748b; }
          .drift-day-label  { fill: #334155; }
          .dark .drift-tick-label { fill: #94a3b8; }
          .dark .drift-day-label  { fill: #e2e8f0; }
        `}</style>
        {/* X-axis grid + labels - tick "h" drawn where hour h UTC lives */}
        {X_TICKS_H.map((h) => {
          const x = minToX(h * 60);
          return (
            <g key={h}>
              <line
                x1={x}
                y1={TOP_MARGIN - 10}
                x2={x}
                y2={svgH - 30}
                stroke="#2d3748"
                strokeDasharray="3 3"
              />
              <text
                x={x}
                y={TOP_MARGIN - 14}
                textAnchor="middle"
                className="drift-tick-label"
                fontSize={12}
                fontWeight={600}
              >
                {String(h).padStart(2, "0")}
              </text>
            </g>
          );
        })}
        {/* End-of-day marker (24:00) - unlabeled boundary line */}
        <line
          x1={minToX(24 * 60)}
          y1={TOP_MARGIN - 10}
          x2={minToX(24 * 60)}
          y2={svgH - 30}
          stroke="#2d3748"
          strokeDasharray="3 3"
        />

        <text
          x={plotStartX + plotW / 2}
          y={svgH - 8}
          textAnchor="middle"
          className="drift-tick-label"
          fontSize={12}
          fontWeight={600}
        >
          Time of day (UTC)
        </text>

        {/* Rows */}
        {days.map((ymd, dayIdx) => {
          const rowY = TOP_MARGIN + dayIdx * ROW_HEIGHT;
          const barY = rowY + (ROW_HEIGHT - BAR_HEIGHT) / 2;
          const dayLabel = fmtDay(ymd);
          // Sort scans by time within the day so we can compute next-scan end time
          const pts = [...(pointsByDay.get(ymd) || [])].sort((a, b) => {
            const aMin =
              new Date(a.timestamp).getUTCHours() * 60 +
              new Date(a.timestamp).getUTCMinutes();
            const bMin =
              new Date(b.timestamp).getUTCHours() * 60 +
              new Date(b.timestamp).getUTCMinutes();
            return aMin - bMin;
          });

          const cy = barY + BAR_HEIGHT / 2;
          const isAlt = dayIdx % 2 === 1;
          return (
            <g key={ymd}>
              {/* Alternating row background band for better separation */}
              {isAlt && (
                <rect
                  x={plotStartX}
                  y={rowY}
                  width={plotW}
                  height={ROW_HEIGHT}
                  fill="#ffffff"
                  fillOpacity={0.015}
                />
              )}

              <text
                x={LEFT_MARGIN - 12}
                y={cy + 4}
                textAnchor="end"
                className="drift-day-label"
                fontSize={13}
                fontWeight={600}
              >
                {dayLabel}
              </text>

              {/* Horizontal grid line for this row */}
              <line
                x1={plotStartX}
                y1={cy}
                x2={plotStartX + plotW}
                y2={cy}
                stroke="#2d3748"
                strokeDasharray="2 4"
                strokeOpacity={0.5}
              />

              {/* Scan diamonds at exact times */}
              {pts.map((h, i) => {
                const d = new Date(h.timestamp);
                const startMin = d.getUTCHours() * 60 + d.getUTCMinutes();
                const cx = minToX(startMin);
                const currentMs = scanTimeMs(h);
                const relatedRaw = pts.filter(
                  (candidate) =>
                    Math.abs(scanTimeMs(candidate) - currentMs) <=
                    SCAN_BATCH_WINDOW_MS,
                );
                const baseColor = h.status === "passed" ? "#2ecc71" : "#f4845f";
                const isMatch =
                  !highlightStatus ||
                  (highlightStatus === "drift"
                    ? driftTimestamps.has(h.timestamp)
                    : h.status === highlightStatus);
                const isSelected =
                  selectedPoint?.raw.timestamp === h.timestamp;
                const r = isSelected ? 10 : isMatch && highlightStatus ? 9 : 8;
                const diamondPoints = `${cx},${cy - r} ${cx + r},${cy} ${cx},${cy + r} ${cx - r},${cy}`;
                const scanPoint: ScanPoint = {
                  x: startMin,
                  y: dayIdx,
                  status: h.status,
                  raw: h,
                  relatedRaw,
                  dayLabel,
                };
                return (
                  <g key={i}>
                    {/* Pulsing halo marks the diamond whose report is open */}
                    {isSelected && (
                      <circle cx={cx} cy={cy} fill="none" stroke="#818cf8">
                        <animate
                          attributeName="r"
                          values={`${r + 4};${r + 10};${r + 4}`}
                          dur="1.6s"
                          repeatCount="indefinite"
                        />
                        <animate
                          attributeName="opacity"
                          values="0.9;0.2;0.9"
                          dur="1.6s"
                          repeatCount="indefinite"
                        />
                        <animate
                          attributeName="stroke-width"
                          values="2.5;1;2.5"
                          dur="1.6s"
                          repeatCount="indefinite"
                        />
                      </circle>
                    )}
                    <polygon
                      points={diamondPoints}
                      fill={baseColor}
                      stroke={
                        isSelected
                          ? "#818cf8"
                          : isMatch && highlightStatus
                            ? h.status === "passed"
                              ? "#16a34a"
                              : "#dc2626"
                            : "var(--diamond-stroke, #0d1117)"
                      }
                      strokeWidth={isSelected ? 2.5 : isMatch && highlightStatus ? 2 : 1.5}
                      opacity={highlightStatus && !isMatch && !isSelected ? 0.2 : 1}
                      style={{
                        cursor: "pointer",
                        transition: "opacity 0.2s, stroke-width 0.2s",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedPoint(scanPoint);
                        setTooltip(null);
                      }}
                      onMouseEnter={(e) => handleBarMouseEnter(e, scanPoint)}
                      onMouseMove={(e) => {
                        const container = containerRef.current;
                        if (!container) return;
                        const rect = container.getBoundingClientRect();
                        setTooltip((prev) =>
                          prev
                            ? {
                                ...prev,
                                mouseX: e.clientX - rect.left,
                                mouseY: e.clientY - rect.top,
                              }
                            : prev,
                        );
                      }}
                    />
                  </g>
                );
              })}
            </g>
          );
        })}
      </svg>

      {/* Hover tooltip */}
      {tooltip &&
        (() => {
          const p = tooltip.point;
          const hh = Math.floor(p.x / 60)
            .toString()
            .padStart(2, "0");
          const mm = (p.x % 60).toString().padStart(2, "0");
          const passed = p.status === "passed";
          const opaCount = p.raw.opaDetails?.length ?? 0;
          // Keep tooltip inside container
          const tipX = Math.min(tooltip.mouseX + 12, width - 220);
          const tipY = tooltip.mouseY - 10;
          return (
            <div
              className="absolute z-30"
              style={{ left: tipX, top: tipY }}
              onMouseEnter={cancelHide}
              onMouseLeave={() => setTooltip(null)}
            >
              <div className="bg-[#0d0f1a] border border-white/[0.12] rounded-xl shadow-2xl p-3 w-52">
                {/* Status + day */}
                <div className="flex items-center gap-2 mb-2">
                  <span
                    className={`w-2 h-2 rounded-full shrink-0 ${passed ? "bg-green-400" : "bg-red-400"}`}
                  />
                  <span
                    className={`text-xs font-semibold ${passed ? "text-green-400" : "text-red-400"}`}
                  >
                    {passed ? "Passed" : "Failed"}
                  </span>
                  <span className="text-xs text-gray-500 ml-auto">
                    {p.dayLabel}
                  </span>
                </div>
                {/* Time */}
                <p className="text-xs text-gray-400 mb-2">
                  {hh}:{mm} UTC
                </p>
                {/* OPA count */}
                {opaCount > 0 && (
                  <p className="text-[11px] text-gray-500 mb-2">
                    {opaCount} automated result{opaCount !== 1 ? "s" : ""}
                  </p>
                )}
                <button
                  className="flex items-center gap-1 text-[11px] text-indigo-400 hover:text-indigo-300 font-medium transition-colors"
                  onClick={() => {
                    setSelectedPoint(p);
                    setTooltip(null);
                  }}
                >
                  Read more <span className="text-[10px]"></span>
                </button>
              </div>
            </div>
          );
        })()}

      {/* Inline scan details - rendered below the graph */}
      {selectedPoint && (
        <ScanDetailsList
          point={selectedPoint}
          onClose={() => setSelectedPoint(null)}
        />
      )}
    </div>
  );
}

// --- Control Drilldown Panel ---

function ControlDrilldownPanel({
  control,
  reviewedControl,
  loading,
  onClose,
}: {
  control: DriftApiControl | null;
  reviewedControl: ApiReviewedControl | null;
  loading: boolean;
  onClose: () => void;
}) {
  const lastScan = useMemo(
    () =>
      control?.history.length
        ? [...control.history].sort((a, b) =>
            b.timestamp.localeCompare(a.timestamp),
          )[0]
        : null,
    [control],
  );

  const [highlightFilter, setHighlightFilter] = useState<
    "passed" | "failed" | "drift" | null
  >(null);
  const primaryStatement = reviewedControl?.statements?.[0];
  const statementText =
    control?.controlStatement || primaryStatement?.statementDescription;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative z-10 w-[70vw] bg-white dark:bg-[#0f1117] border-l border-gray-200 dark:border-white/10 h-full overflow-y-auto flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200 dark:border-white/10 sticky top-0 z-20 bg-white dark:bg-[#0f1117]">
          <div className="space-y-1 pr-4">
            {loading ? (
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                Loading drift data
              </h3>
            ) : (
              <>
                <div className="space-y-2">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white leading-snug">
                    {control?.controlTitle || reviewedControl?.controlDescription || reviewedControl?.controlId?.toUpperCase() || control?.controlId?.toUpperCase()}
                  </h3>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-mono font-bold text-indigo-400 shrink-0 leading-none">
                      {(reviewedControl?.controlId || control?.controlId)?.toUpperCase()}
                    </span>
                    <CtrlStatusBadge status={control?.currentStatus} />
                  </div>
                </div>
              </>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors shrink-0"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center space-y-3">
              <Loader2 className="w-10 h-10 animate-spin text-indigo-400 mx-auto" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Running drift analysis
              </p>
            </div>
          </div>
        ) : control ? (
          <div className="p-6 space-y-6 flex-1">
            {/* Statement */}
            {statementText && (
              <div className="bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl p-4 space-y-3">
                <p className="text-sm font-semibold text-gray-600 dark:text-gray-300 tracking-wide">
                  Control Statement
                </p>
                {primaryStatement?.statementId && (
                  <p className="text-sm font-mono text-indigo-600 dark:text-indigo-300">
                    {primaryStatement.statementId?.toUpperCase()}
                  </p>
                )}
                <p className="text-[15px] text-gray-800 dark:text-gray-100 leading-relaxed">
                  {statementText}
                </p>
              </div>
            )}

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                {
                  label: "Passed",
                  value: control.history.filter((h) => h.status === "passed")
                    .length,
                  icon: CheckCircle2,
                  color: "bg-emerald-500/20 text-emerald-400",
                  filter: "passed" as const,
                },
                {
                  label: "Failed",
                  value: control.history.filter((h) => h.status === "failed")
                    .length,
                  icon: XCircle,
                  color: "bg-red-500/20 text-red-400",
                  filter: "failed" as const,
                },
                {
                  label: "Drift Count",
                  value: control.driftCount === 0 ? "No drift" : control.driftCount,
                  icon: TrendingDown,
                  color: "bg-orange-500/20 text-orange-400",
                  filter: "drift" as const,
                },
                {
                  label: "Last scanned Control Stability status",
                  value: lastScan ? fmtDate(lastScan.timestamp) : "-",
                  icon: Clock,
                  color:
                    lastScan?.status === "passed"
                      ? "bg-emerald-500/20 text-emerald-400"
                      : "bg-red-500/20 text-red-400",
                  filter: null,
                },
              ].map((s) => (
                <div
                  key={s.label}
                  className={`cursor-pointer rounded-xl transition-all ${highlightFilter === s.filter && s.filter ? "ring-2 ring-indigo-400/50 dark:ring-indigo-400/50" : ""}`}
                  onClick={() => {
                    if (s.filter)
                      setHighlightFilter((prev) =>
                        prev === s.filter ? null : s.filter,
                      );
                  }}
                >
                  <KpiCard
                    label={s.label}
                    value={s.value}
                    icon={s.icon}
                    colorCls={s.color}
                  />
                </div>
              ))}
            </div>

            {/* Finding Timeline */}
            <div>
              <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-1">
                Finding Timeline
              </h4>
              <div className="flex items-center gap-2 mb-3 text-sm rounded-lg bg-indigo-500/[0.06] border border-indigo-500/15 px-3 py-2 text-gray-800 dark:text-gray-100">
                <MousePointerClick className="size-4 text-indigo-500 shrink-0" />
                <span>
                  <span className="font-semibold">Click any diamond</span> to
                  open its scan report below. Each diamond is one assessment run.
                </span>
              </div>
              <ComplianceShiftMatrix
                history={control.history}
                highlightStatus={highlightFilter}
              />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

// --- Drift Control Card ---

// Cap the inline scan strip so very dense schedules stay readable; full history
// lives in the drill-down. Shows the most recent N scans.
const MAX_SCAN_SQUARES = 60;

function fmtScanLabel(iso: string): string {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    const mon = d.toLocaleString("en-US", { month: "short" });
    const day = d.getUTCDate();
    const hh = String(d.getUTCHours()).padStart(2, "0");
    const mm = String(d.getUTCMinutes()).padStart(2, "0");
    return `${day} ${mon} ${hh}:${mm}`;
  } catch {
    return iso;
  }
}

function StatTile({
  icon: Icon,
  value,
  label,
  chipCls,
}: {
  icon: ElementType;
  value: number | string;
  label: string;
  chipCls: string;
}) {
  return (
    <div className="rounded-lg border border-border/60 bg-muted/30 dark:bg-white/[0.02] px-1 py-2 flex flex-col items-center gap-1">
      <div className={`size-5 rounded-md grid place-items-center ${chipCls}`}>
        <Icon size={11} />
      </div>
      <span className="text-sm font-bold text-foreground leading-none">
        {value}
      </span>
      <span className="text-[8px] font-semibold tracking-wider text-foreground/45">
        {label}
      </span>
    </div>
  );
}

const SCAN_CHART_H = 44;
// Pass bars are taller, fail bars are shorter — consistent heights per status
// so the rhythm of passes vs fails is immediately readable.
const PASS_BAR_H = 70;
const FAIL_BAR_H = 55;
const UNKNOWN_BAR_H = 25;

function ScanBarChart({
  scans,
  showLine: _showLine,
}: {
  scans: { timestamp: string; status: string }[];
  showLine: boolean;
}) {
  if (scans.length === 0) {
    return (
      <div
        className="grid place-items-center rounded-lg border border-dashed border-border/60 text-[10px] d"
        style={{ height: SCAN_CHART_H }}
      >
        No scans yet
      </div>
    );
  }

  return (
    <div className="flex items-end gap-[2px]" style={{ height: SCAN_CHART_H }}>
      {scans.map((s, i) => {
        const h =
          s.status === "passed"
            ? PASS_BAR_H
            : s.status === "failed"
              ? FAIL_BAR_H
              : UNKNOWN_BAR_H;
        const colorCls =
          s.status === "passed"
            ? "bg-emerald-700/50"
            : s.status === "failed"
              ? "bg-rose-500/60"
              : "bg-gray-400/20";
        const isPassed = s.status === "passed";
        return (
          <Tooltip key={i}>
            <TooltipTrigger asChild>
              <div
                className={`flex-1 min-w-0 rounded-[1px] border border-white/[0.06] cursor-default ${colorCls}`}
                style={{ height: `${h}%` }}
              />
            </TooltipTrigger>
            <TooltipContent side="top" className="flex flex-col items-center gap-0.5 px-2.5 py-1.5 text-[11px]">
              <span className={`font-bold ${isPassed ? "text-emerald-300" : "text-rose-300"}`}>
                {isPassed ? "✓ Passed" : "✗ Failed"}
              </span>
              <span className="text-white font-medium">{fmtScanLabel(s.timestamp)}</span>
            </TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
}

function DriftControlCard({
  reviewed,
  driftCtrl,
  onClick,
  showTrendLine,
}: {
  reviewed: ApiReviewedControl;
  driftCtrl: DriftApiControl | undefined;
  onClick: () => void;
  showTrendLine: boolean;
}) {
  const history = useMemo(
    () =>
      [...(driftCtrl?.history ?? [])].sort((a, b) =>
        a.timestamp.localeCompare(b.timestamp),
      ),
    [driftCtrl],
  );

  // Read the backend-shipped scan rollup counts (no need to walk history).
  const passedScans = driftCtrl?.passedScans ?? 0;
  const failedScans = driftCtrl?.failedScans ?? 0;
  const unknownScans = driftCtrl?.unknownScans ?? 0;
  const totalScans = driftCtrl?.totalScans ?? history.length;
  const conclusive = passedScans + failedScans;
  const passRate =
    conclusive > 0 ? Math.round((passedScans / totalScans) * 100) : 0;
  const driftCount = driftCtrl?.driftCount ?? 0;
  const streak = driftCtrl?.longestFailedStreak ?? 0;
  const status = driftCtrl?.currentStatus ?? "unknown";
  const lastScan = history[history.length - 1];
  // Chart shows conclusive scans only - drop unknown/not-applicable so the
  // trajectory reads as a clean pass/fail rhythm.
  const squares = history
    .filter((h) => h.status === "passed" || h.status === "failed")
    .slice(-MAX_SCAN_SQUARES);

  // The donut is a "% pass" gauge, so color it by the pass rate itself - not
  // by the overall pass/fail status. Otherwise a 69%-pass ring on a "Failed"
  // control gets painted red, which misreads as "69% is failing".
  const rateColor =
    conclusive === 0
      ? "text-foreground/60"
      : passRate >= 70
        ? "text-emerald-600 dark:text-emerald-400"
        : passRate >= 40
          ? "text-amber-600 dark:text-amber-400"
          : "text-rose-600 dark:text-rose-400";
  // Donut segments: green for the pass share, red for the fail share, taken
  // over conclusive scans so the two arcs always fill the full ring.
  const passSeg = conclusive > 0 ? (passedScans / conclusive) * 100 : 0;
  const failSeg = conclusive > 0 ? 100 - passSeg : 0;
  const borderCls =
    status === "passed"
      ? "border-emerald-500/20 hover:border-emerald-500/45"
      : status === "failed"
        ? "border-rose-500/20 hover:border-rose-500/45"
        : "border-border hover:border-indigo-400/50";
  const washCls = "from-transparent";

  return (
    <button
      type="button"
      onClick={onClick}
      className={`group relative text-left rounded-2xl border ${borderCls} bg-card hover:shadow-lg hover:shadow-black/5 dark:hover:shadow-black/25 transition-all overflow-hidden flex flex-col`}
    >
      {/* Faint status wash */}
      <div
        className={`pointer-events-none absolute inset-x-0 top-0 h-20 bg-gradient-to-b ${washCls} to-transparent`}
      />

      <div className="relative p-4 flex flex-col gap-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 min-h-[58px] flex flex-col">
            <p className="font-bold text-foreground line-clamp-2 leading-snug">
              {driftCtrl?.controlTitle || reviewed.controlDescription || reviewed.controlId?.toUpperCase()}
            </p>
            <span className="text-sm font-mono font-semibold text-indigo-600 dark:text-indigo-400 mt-3 block">
              {reviewed.controlId?.toUpperCase()}
            </span>
          </div>
          <div className="shrink-0 relative size-[64px]">
            <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
              {/* Track */}
              <circle cx="18" cy="18" r="15.5" fill="none" strokeWidth="3" pathLength={100} className="stroke-border/40" />
              {conclusive > 0 && (
                <>
                  {/* Pass share */}
                  <circle
                    cx="18" cy="18" r="15.5" fill="none" strokeWidth="3" pathLength={100}
                    strokeDasharray={`${passSeg} ${100 - passSeg}`}
                    className="stroke-emerald-500"
                  />
                  {/* Fail share */}
                  <circle
                    cx="18" cy="18" r="15.5" fill="none" strokeWidth="3" pathLength={100}
                    strokeDasharray={`${failSeg} ${100 - failSeg}`}
                    strokeDashoffset={`-${passSeg}`}
                    className="stroke-rose-500"
                  />
                </>
              )}
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className={`text-[13px] font-bold leading-none ${conclusive > 0 ? rateColor : "text-foreground/30"}`}>
                {conclusive > 0 ? `${passRate}%` : "-"}
              </span>
            </div>
          </div>
        </div>

        {/* Stat row */}
        <div className="flex items-center rounded-lg overflow-hidden border border-border/50 divide-x divide-border/50">
          <div className="flex-1 flex flex-col items-center py-2 gap-0.5 bg-emerald-500/[0.04]">
            <span className="text-base font-bold text-emerald-500 leading-none">{passedScans}</span>
            <span className="text-[9px] font-semibold tracking-wider text-emerald-600/60 dark:text-emerald-400/50">Pass</span>
          </div>
          <div className="flex-1 flex flex-col items-center py-2 gap-0.5 bg-rose-500/[0.04]">
            <span className="text-base font-bold text-rose-500 leading-none">{failedScans}</span>
            <span className="text-[9px] font-semibold tracking-wider text-rose-600/60 dark:text-rose-400/50">Fail</span>
          </div>
          <div className="flex-1 flex flex-col items-center py-2 gap-0.5">
            <span className="text-base font-bold text-foreground/70 leading-none">{driftCount}</span>
            <span className="text-[9px] font-semibold tracking-wider text-foreground/35">Drift</span>
          </div>
        </div>

        {/* Scan bar chart */}
        <div>
          <ScanBarChart scans={squares} showLine={showTrendLine} />
          <div className="grid grid-cols-2 gap-3 mt-2 text-[11px]">
            <div className="min-w-0 flex items-baseline gap-1.5">
              <span className="shrink-0 text-[11px] font-semibold tracking-normal text-foreground/80">
                Total scans:
              </span>
              <span className="min-w-0 truncate font-bold text-foreground">
                {totalScans > 0
                  ? `${totalScans} ${
                      unknownScans > 0 ? ` - ${unknownScans} unknown` : ""
                    }`
                  : "No scans yet"}
              </span>
            </div>
            <div className="min-w-0 flex items-baseline justify-end gap-1.5 text-right">
              <span className="shrink-0 text-[11px] font-semibold tracking-normal text-foreground/80">
                Last scan date:
              </span>
              <span className="min-w-0 truncate font-bold text-foreground">
                {lastScan ? fmtScanLabel(lastScan.timestamp) : "-"}
              </span>
            </div>
          </div>
        </div>
      </div>
    </button>
  );
}

// --- Drift Date Range Picker ---

// ── Drift Plan Dropdown ───────────────────────────────────────────────────────

function DriftPlanDropdown({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = options.find((o) => o.value === value);

  const filtered = search.trim()
    ? options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;

  useEffect(() => {
    if (!open) return;
    setSearch("");
    setTimeout(() => inputRef.current?.focus(), 50);
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      {/* Trigger */}
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className={`h-[38px] min-w-[140px] max-w-[220px] inline-flex items-center justify-between gap-2 px-3 rounded-lg border text-sm transition-all outline-none cursor-pointer
              ${open
                ? "border-indigo-500 ring-2 ring-indigo-500/20 bg-card"
                : "border-border bg-card hover:border-indigo-400/60 dark:hover:border-indigo-400/40"
              } text-foreground`}
          >
            <span className="truncate flex-1 text-left">{selected?.label ?? "All (SSP Level)"}</span>
            <ChevronRight className={`size-4 shrink-0 text-foreground/50 transition-transform duration-200 ${open ? "-rotate-90" : "rotate-90"}`} />
          </button>
        </TooltipTrigger>
        {(selected?.label ?? "").length > 18 && (
          <TooltipContent
            side="top"
            className="z-[300] max-w-[260px] rounded-lg px-3 py-1.5 text-xs font-medium bg-[#1e293b] dark:bg-[#0f172a] text-white border border-white/10 shadow-xl"
          >
            {selected?.label}
          </TooltipContent>
        )}
      </Tooltip>

      {/* Popover */}
      {open && (
        <div className="absolute top-full left-0 mt-1.5 z-[200] w-[280px] rounded-xl border border-border bg-popover shadow-xl overflow-hidden">
          {/* Search */}
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-foreground/40 pointer-events-none" />
              <input
                ref={inputRef}
                type="text"
                placeholder="Search plans"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-xs bg-background border border-border rounded-lg outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/20 placeholder:text-foreground/30 text-foreground transition-all"
              />
            </div>
          </div>

          {/* List — fixed height, scrolls after 5 items */}
          <div
            className="p-1 overflow-y-auto [scrollbar-width:thin] [scrollbar-color:rgba(148,163,184,0.4)_transparent] [&::-webkit-scrollbar]:w-[4px] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-slate-400/40 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-slate-400/70"
            style={{ maxHeight: `${5 * 36}px` }}
          >
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-xs text-center text-foreground/40">No plans found</div>
            ) : (
              filtered.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => { onChange(opt.value); setOpen(false); }}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm rounded-lg transition-colors text-left
                    ${opt.value === value
                      ? "bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 font-semibold"
                      : "text-[#334155] dark:text-white/70 hover:bg-indigo-500/10 hover:text-indigo-600 dark:hover:text-white"
                    }`}
                  style={{ minHeight: 36 }}
                >
                  <span className="break-words">{opt.label}</span>
                  {opt.value === value && <Check className="size-3.5 shrink-0 text-indigo-600 dark:text-indigo-400" />}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Custom Range Calendar ─────────────────────────────────────────────────────

const WEEK_DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function ymd(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function parseYmd(s: string): Date {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function addMonths(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(1);
  r.setMonth(r.getMonth() + n);
  return r;
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function daysInMonth(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
}

function buildCalendarGrid(month: Date): (Date | null)[] {
  const first = startOfMonth(month);
  const totalDays = daysInMonth(month);
  const startDow = first.getDay();
  const grid: (Date | null)[] = [];
  for (let i = 0; i < startDow; i++) grid.push(null);
  for (let d = 1; d <= totalDays; d++) grid.push(new Date(month.getFullYear(), month.getMonth(), d));
  while (grid.length % 7 !== 0) grid.push(null);
  return grid;
}

function MonthCalendar({
  month,
  startYmd,
  endYmd,
  hoverYmd,
  selectingEnd,
  today,
  maxDate,
  onDayClick,
  onDayHover,
}: {
  month: Date;
  startYmd: string;
  endYmd: string;
  hoverYmd: string;
  selectingEnd: boolean;
  today: string;
  maxDate: string;
  onDayClick: (d: string) => void;
  onDayHover: (d: string) => void;
}) {
  const grid = buildCalendarGrid(month);
  const monthLabel = month.toLocaleString("en-US", { month: "long", year: "numeric" });

  // Determine preview range end (hover preview while selecting end)
  const previewEnd = selectingEnd && hoverYmd && startYmd ? hoverYmd : "";

  function getState(d: Date) {
    const ds = ymd(d);
    const isStart = ds === startYmd;
    const isEnd = ds === endYmd;
    const isToday = ds === today;
    const isDisabled = ds > maxDate;
    const isOutside = d.getMonth() !== month.getMonth();

    // Confirmed range
    let inRange = false;
    if (startYmd && endYmd) {
      const lo = startYmd < endYmd ? startYmd : endYmd;
      const hi = startYmd < endYmd ? endYmd : startYmd;
      inRange = ds > lo && ds < hi;
    }

    // Hover preview range
    let inPreview = false;
    if (selectingEnd && startYmd && previewEnd && !endYmd) {
      const lo = startYmd < previewEnd ? startYmd : previewEnd;
      const hi = startYmd < previewEnd ? previewEnd : startYmd;
      inPreview = ds > lo && ds < hi;
    }

    // Range edge alignment helpers (for square/round CSS trick)
    const rangeStart = startYmd && endYmd ? (startYmd < endYmd ? startYmd : endYmd) : startYmd;
    const rangeEnd   = startYmd && endYmd ? (startYmd < endYmd ? endYmd : startYmd) : (previewEnd || "");
    const isRangeStart = ds === rangeStart;
    const isRangeEnd   = ds === rangeEnd;

    return { ds, isStart, isEnd, isToday, isDisabled, isOutside, inRange, inPreview, isRangeStart, isRangeEnd };
  }

  return (
    <div className="select-none">
      <div className="text-center text-[13px] font-bold text-[#0f172a] dark:text-white/90 mb-2 px-2">
        {monthLabel}
      </div>
      <div className="grid grid-cols-7 mb-0.5">
        {WEEK_DAYS.map((w) => (
          <div key={w} className="text-center text-[11px] font-semibold text-[#94a3b8] dark:text-white/35 py-0.5">
            {w}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {grid.map((d, i) => {
          if (!d) return <div key={`e-${i}`} />;
          const { ds, isStart, isEnd, isToday, isDisabled, isOutside, inRange, inPreview, isRangeStart, isRangeEnd } = getState(d);

          const isSelected = isStart || isEnd;
          const isInAnyRange = inRange || inPreview;
          // Start/end dates also need the range strip on their inner half
          const needsRightHalf = isRangeStart && !isRangeEnd && (isStart || isEnd);
          const needsLeftHalf  = isRangeEnd   && !isRangeStart && (isStart || isEnd);

          const RANGE_BG = "bg-indigo-100 dark:bg-[#1e2d4d]";

          return (
            <div
              key={ds}
              className="relative h-8 flex items-center justify-center"
              onMouseEnter={() => !isDisabled && !isOutside && onDayHover(ds)}
            >
              {/* Full-bleed range strip for middle dates */}
              {isInAnyRange && (
                <div className={`absolute inset-y-0 left-0 right-0 ${RANGE_BG} transition-colors duration-150`} />
              )}
              {/* Half-bleed strip extending from start date rightward */}
              {needsRightHalf && (
                <div className={`absolute inset-y-0 left-1/2 right-0 ${RANGE_BG} transition-colors duration-150`} />
              )}
              {/* Half-bleed strip extending into end date from left */}
              {needsLeftHalf && (
                <div className={`absolute inset-y-0 left-0 right-1/2 ${RANGE_BG} transition-colors duration-150`} />
              )}

              <button
                type="button"
                onClick={() => !isDisabled && !isOutside && onDayClick(ds)}
                disabled={isDisabled}
                className={[
                  "relative z-10 w-full h-full flex items-center justify-center text-[12px] font-medium transition-all duration-150 outline-none",
                  isSelected
                    ? "bg-indigo-600 dark:bg-indigo-500 text-white font-semibold"
                    : isDisabled
                    ? "opacity-25 cursor-not-allowed text-[#94a3b8] dark:text-white/30"
                    : isOutside
                    ? "text-[#cbd5e1] dark:text-white/20 cursor-default"
                    : isInAnyRange
                    ? "text-indigo-700 dark:text-indigo-200"
                    : "text-[#334155] dark:text-white/75 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.07]",
                ].join(" ")}
              >
                {d.getDate()}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DriftDateRangePicker({
  start,
  end,
  loading,
  onStartChange,
  onEndChange,
  onFilter,
}: {
  start: string;
  end: string;
  loading: boolean;
  onStartChange: (value: string) => void;
  onEndChange: (value: string) => void;
  onFilter: (start: string, end: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  // Which month each panel shows
  const todayDate = new Date();
  const [leftMonth, setLeftMonth] = useState(() => {
    if (start) return startOfMonth(parseYmd(start));
    const m = new Date(todayDate);
    m.setMonth(m.getMonth() - 1);
    m.setDate(1);
    return m;
  });
  // Internal picking state
  const [pickStart, setPickStart] = useState(start);
  const [pickEnd, setPickEnd] = useState(end);
  const [selectingEnd, setSelectingEnd] = useState(false);
  const [hoverDay, setHoverDay] = useState("");

  const todayYmd = ymd(todayDate);
  const maxDate = todayYmd;

  // Sync external → internal when picker opens
  useEffect(() => {
    if (open) {
      setPickStart(start);
      setPickEnd(end);
      setSelectingEnd(false);
      setHoverDay("");
      if (start) setLeftMonth(startOfMonth(parseYmd(start)));
      else {
        const m = new Date(todayDate);
        m.setMonth(m.getMonth() - 1);
        m.setDate(1);
        setLeftMonth(m);
      }
    }
  }, [open]);

  const rightMonth = addMonths(leftMonth, 1);

  const fmt = (s: string) =>
    s ? parseYmd(s).toLocaleDateString("en-GB") : null;

  const handleDayClick = useCallback(
    (ds: string) => {
      if (!selectingEnd) {
        // First click → set start, enter selecting-end mode
        setPickStart(ds);
        setPickEnd("");
        setSelectingEnd(true);
        setHoverDay("");
      } else {
        // Second click → set end
        let s = pickStart;
        let e = ds;
        if (s > e) [s, e] = [e, s];
        setPickStart(s);
        setPickEnd(e);
        setSelectingEnd(false);
        setHoverDay("");
        onStartChange(s);
        onEndChange(e);
        setOpen(false);
      }
    },
    [selectingEnd, pickStart, onStartChange, onEndChange],
  );

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const hasDate = !!start || !!end;
  const label = hasDate
    ? `${fmt(start) ?? "DD/MM/YYYY"} – ${fmt(end) ?? "DD/MM/YYYY"}`
    : "Select date range";

  const clearAll = () => {
    setPickStart("");
    setPickEnd("");
    setSelectingEnd(false);
    onStartChange("");
    onEndChange("");
    onFilter("", "");
    setOpen(false);
  };

  return (
    <div className="relative flex items-center gap-2 flex-shrink-0" ref={ref}>
      {/* Trigger */}
      <button
        onClick={() => !loading && setOpen((v) => !v)}
        disabled={loading}
        className={`h-[38px] flex items-center gap-2 px-3 rounded-lg border text-sm font-medium transition-all outline-none select-none
          ${open
            ? "border-indigo-500 ring-2 ring-indigo-500/20 bg-white dark:bg-white/[0.06]"
            : "border-[#e2e8f0] dark:border-white/[0.07] bg-white dark:bg-white/[0.04] hover:border-indigo-400 dark:hover:border-indigo-500/50"
          }
          ${hasDate ? "text-[#0f172a] dark:text-white" : "text-[#94a3b8] dark:text-white/40"}
          disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        <CalendarDays className="size-3.5 text-indigo-500 dark:text-indigo-400 shrink-0" />
        <span className="whitespace-nowrap">{label}</span>
        {hasDate && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); clearAll(); }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); clearAll(); } }}
            className="ml-0.5 flex items-center justify-center text-[#94a3b8] hover:text-red-500 dark:text-white/30 dark:hover:text-red-400 transition-colors"
          >
            <X className="size-3" />
          </span>
        )}
      </button>

      {/* Filter button */}
      <button
        onClick={() => onFilter(start, end)}
        disabled={!hasDate || loading}
        className="h-[38px] px-4 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold transition-all disabled:opacity-45 disabled:pointer-events-none inline-flex items-center gap-1.5"
      >
        {loading && <Loader2 className="size-3.5 animate-spin" />}
        {loading ? "Filtering…" : "Filter"}
      </button>

      {/* Calendar popover */}
      {open && (
        <div className="absolute top-full left-0 mt-2 z-50 rounded-2xl border border-[#e2e8f0] dark:border-white/[0.08] bg-white dark:bg-[#16162a] shadow-2xl shadow-black/10 dark:shadow-black/50 overflow-hidden">
          {/* Hint */}
          <div className="px-4 pt-3 pb-1.5 flex items-center justify-between">
            <span className="text-[11px] font-medium text-[#64748b] dark:text-white/40">
              {selectingEnd ? "Now click an end date" : "Click a start date"}
            </span>
            {(pickStart || pickEnd) && (
              <button onClick={clearAll} className="text-[11px] text-red-500 hover:text-red-600 dark:text-red-400 font-semibold transition-colors">
                Clear
              </button>
            )}
          </div>

          {/* Dual month grid */}
          <div className="flex gap-0 px-3 pb-3" onMouseLeave={() => setHoverDay("")}>
            <div className="w-[230px]">
              {/* Nav */}
              <div className="flex items-center justify-between mb-1.5 px-1">
                <button
                  onClick={() => setLeftMonth((m) => addMonths(m, -1))}
                  className="p-1 rounded-lg text-[#64748b] dark:text-white/40 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.07] hover:text-[#0f172a] dark:hover:text-white transition-colors"
                  aria-label="Previous month"
                >
                  <ChevronRight className="size-3.5 rotate-180" />
                </button>
                <span />
              </div>
              <MonthCalendar
                month={leftMonth}
                startYmd={pickStart}
                endYmd={pickEnd}
                hoverYmd={hoverDay}
                selectingEnd={selectingEnd}
                today={todayYmd}
                maxDate={maxDate}
                onDayClick={handleDayClick}
                onDayHover={setHoverDay}
              />
            </div>

            <div className="w-px bg-[#f1f5f9] dark:bg-white/[0.05] mx-2.5 self-stretch" />

            <div className="w-[230px]">
              <div className="flex items-center justify-between mb-1.5 px-1">
                <span />
                <button
                  onClick={() => setLeftMonth((m) => addMonths(m, 1))}
                  className="p-1 rounded-lg text-[#64748b] dark:text-white/40 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.07] hover:text-[#0f172a] dark:hover:text-white transition-colors"
                  aria-label="Next month"
                >
                  <ChevronRight className="size-3.5" />
                </button>
              </div>
              <MonthCalendar
                month={rightMonth}
                startYmd={pickStart}
                endYmd={pickEnd}
                hoverYmd={hoverDay}
                selectingEnd={selectingEnd}
                today={todayYmd}
                maxDate={maxDate}
                onDayClick={handleDayClick}
                onDayHover={setHoverDay}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Controls View ---

function ControlsView({
  plan,
  controls,
  loading,
  filtering,
  driftControlMap,
  summary,
  filterStartDate,
  filterEndDate,
  onFilterStartDateChange,
  onFilterEndDateChange,
  onControlClick,
  onDateFilter,
  sspPlans,
  selectedPlanFilter,
  onPlanFilterChange,
}: {
  plan?: ApiAssessmentPlan | null;
  controls: ApiReviewedControl[];
  loading: boolean;
  filtering: boolean;
  driftControlMap: Map<string, DriftApiControl>;
  summary: DriftSummary | null;
  filterStartDate: string;
  filterEndDate: string;
  onFilterStartDateChange: (value: string) => void;
  onFilterEndDateChange: (value: string) => void;
  onControlClick: (c: ApiReviewedControl) => void;
  onDateFilter: (start: string, end: string) => void;
  sspPlans?: ApiAssessmentPlan[];
  selectedPlanFilter?: string | null;
  onPlanFilterChange?: (planId: string | null) => void;
}) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(12);
  // Scan-chart style: trend line over the bars, or bars only.
  const [showTrendLine, setShowTrendLine] = useState(true);

  const driftControls = useMemo(
    () => controls.filter((c) => driftControlMap.has(c.controlId)),
    [controls, driftControlMap],
  );

  const filteredControls = useMemo(() => {
    if (!search.trim()) return driftControls;
    const q = search.toLowerCase();
    return driftControls.filter(
      (c) =>
        c.controlId?.toLowerCase().includes(q) ||
        c.controlDescription?.toLowerCase().includes(q) ||
        c.statements?.[0]?.statementDescription?.toLowerCase().includes(q),
    );
  }, [driftControls, search]);

  // Default order: most drift first, then control id.
  const sortedControls = useMemo(() => {
    return [...filteredControls].sort((a, b) => {
      const da = driftControlMap.get(a.controlId)?.driftCount ?? -1;
      const db = driftControlMap.get(b.controlId)?.driftCount ?? -1;
      if (db !== da) return db - da;
      return (a.controlId || "").localeCompare(b.controlId || "");
    });
  }, [filteredControls, driftControlMap]);

  const pagedControls = useMemo(
    () => sortedControls.slice(page * pageSize, page * pageSize + pageSize),
    [sortedControls, page, pageSize],
  );

  // Prefer the backend-computed summary; fall back to deriving from the controls.
  const kpis = useMemo(() => {
    if (summary) {
      return {
        total: summary.totalControls,
        passed: summary.passedControls,
        failed: summary.failedControls,
        drifts: summary.totalDrifts,
      };
    }
    let passed = 0;
    let failed = 0;
    let drifts = 0;
    driftControlMap.forEach((c) => {
      if (c.currentStatus === "passed") passed++;
      else if (c.currentStatus === "failed") failed++;
      drifts += c.driftCount ?? 0;
    });
    return { total: driftControlMap.size, passed, failed, drifts };
  }, [summary, driftControlMap]);

  const totalPages = Math.max(1, Math.ceil(filteredControls.length / pageSize));
  const start = filteredControls.length === 0 ? 0 : page * pageSize + 1;
  const end = Math.min(filteredControls.length, page * pageSize + pageSize);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-32 mt-4">
        <Loader2 className="size-9 animate-spin text-indigo-400" />
        <p className="text-sm d">Loading drift data…</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 mt-4">
      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard
          label="Total Controls"
          value={kpis.total}
          icon={Activity}
          colorCls="bg-indigo-500/20 text-indigo-400"
        />
        <KpiCard
          label="Passed"
          value={kpis.passed}
          icon={CheckCircle2}
          colorCls="bg-emerald-500/20 text-emerald-400"
        />
        <KpiCard
          label="Failed"
          value={kpis.failed}
          icon={XCircle}
          colorCls="bg-red-500/20 text-red-400"
        />
        <KpiCard
          label="Drift Events"
          value={kpis.drifts}
          icon={GitBranch}
          colorCls="bg-amber-500/20 text-amber-400"
        />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        {/* Search */}
        <div className="relative flex-1 min-w-[180px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 d/50 pointer-events-none" />
          <input
            type="text"
            placeholder="Search controls"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            className="w-full pl-9 pr-4 py-2 text-sm bg-card border border-border rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 dark:focus:ring-indigo-500/20 focus:border-indigo-500 dark:focus:border-indigo-500 transition-all placeholder:d/50"
          />
        </div>

        {/* Plan filter — shown right after search, only when viewing an SSP with plans */}
        {sspPlans && sspPlans.length > 0 && onPlanFilterChange && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-[13px] font-bold text-[#334155] dark:text-white/90 tracking-wide whitespace-nowrap">
              Plan
            </span>
            <DriftPlanDropdown
              value={selectedPlanFilter ?? ""}
              onChange={(v) => onPlanFilterChange(v === "" ? null : v)}
              options={[
                { value: "", label: "All (SSP Level)" },
                ...sspPlans.map((p) => ({
                  value: p.id,
                  label: p.title || p.documentId || p.id,
                })),
              ]}
            />
          </div>
        )}

        {/* Date range picker */}
        <DriftDateRangePicker
          start={filterStartDate}
          end={filterEndDate}
          loading={filtering}
          onStartChange={onFilterStartDateChange}
          onEndChange={onFilterEndDateChange}
          onFilter={onDateFilter}
        />

        {/* Cards per page */}
        <div className="flex items-center gap-2 ml-auto flex-shrink-0">
          <label className="text-sm d whitespace-nowrap">Cards per page</label>
          <Dropdown
            value={String(pageSize)}
            onChange={(v) => {
              setPageSize(Number(v));
              setPage(0);
            }}
            options={[6, 12, 24, 48, 96].map((n) => ({
              value: String(n),
              label: String(n),
            }))}
            size="sm"
            align="end"
            className="!min-w-[80px]"
          />
        </div>
      </div>

      <div className="relative min-h-[220px]">
        <div className={filtering ? "pointer-events-none opacity-45 transition-opacity" : "transition-opacity"}>
          {pagedControls.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border py-16 text-center text-sm d">
              {driftControls.length === 0
                ? "No drift data available for this plan."
                : "No controls match your search."}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {pagedControls.map((r) => (
                  <DriftControlCard
                    key={r.id}
                    reviewed={r}
                    driftCtrl={driftControlMap.get(r.controlId)}
                    onClick={() => onControlClick(r)}
                    showTrendLine={showTrendLine}
                  />
                ))}
              </div>

              {filteredControls.length > pageSize && (
                <div className="flex items-center justify-between pt-1">
                  <span className="text-xs d">
                    Showing {start}-{end} of {filteredControls.length} controls
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                      disabled={page === 0}
                      className="px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-accent disabled:opacity-40 disabled:pointer-events-none transition-colors"
                    >
                      Prev
                    </button>
                    <span className="text-xs d">
                      {page + 1} / {totalPages}
                    </span>
                    <button
                      onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                      disabled={page >= totalPages - 1}
                      className="px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-accent disabled:opacity-40 disabled:pointer-events-none transition-colors"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        {filtering && (
          <div className="absolute inset-0 z-10 grid place-items-center rounded-2xl bg-background/45 backdrop-blur-[1px]">
            <div className="flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-3 shadow-lg">
              <Loader2 className="size-5 animate-spin text-indigo-400" />
              <span className="text-sm font-medium text-foreground">
                Loading
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// --- Main Component ---

type View = "plans" | "controls";

export function DriftAnalysisWorkspace() {
  const [view, setView] = useState<View>("plans");

  const [ssps, setSsps] = useState<SspSummaryResponse[]>([]);
  const [sspMap, setSspMap] = useState<Map<string, string>>(new Map());
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState<string | null>(null);

  const [selectedSsp, setSelectedSsp] = useState<SspSummaryResponse | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<ApiAssessmentPlan | null>(
    null,
  );
  const [reviewedControls, setReviewedControls] = useState<
    ApiReviewedControl[]
  >([]);
  const [driftControlMap, setDriftControlMap] = useState<
    Map<string, DriftApiControl>
  >(new Map());
  const [driftSummary, setDriftSummary] = useState<DriftSummary | null>(null);
  const [controlsLoading, setControlsLoading] = useState(false);
  const [driftFilteringLoading, setDriftFilteringLoading] = useState(false);
  const [driftFilterStart, setDriftFilterStart] = useState("");
  const [driftFilterEnd, setDriftFilterEnd] = useState("");
  const [controlsError, setControlsError] = useState<string | null>(null);

  const [sspPlans, setSspPlans] = useState<ApiAssessmentPlan[]>([]);
  const [selectedPlanFilter, setSelectedPlanFilter] = useState<string | null>(null);

  const [drilldownOpen, setDrilldownOpen] = useState(false);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [selectedDriftControl, setSelectedDriftControl] =
    useState<DriftApiControl | null>(null);
  const [selectedReviewedControl, setSelectedReviewedControl] =
    useState<ApiReviewedControl | null>(null);

  const [planSearch, setPlanSearch] = useState("");
  const [planPage, setPlanPage] = useState(0);
  const [planViewMode, setPlanViewMode] = useState<"card" | "table">("card");
  // Must be a valid option for the default view mode above ("card") —
  // the plain table default (10) isn't one of [12,24,48,96].
  const [planPageSize, setPlanPageSize] = useState(getPageSizeOptions("card")[0]);
  const [planSortKey, setPlanSortKey] = useState<string | null>(null);
  const [planSortDir, setPlanSortDir] = useState<"asc" | "desc">("asc");

  const handlePlanSort = (key: string) => {
    if (planSortKey === key) {
      setPlanSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setPlanSortKey(key);
      setPlanSortDir("asc");
    }
    setPlanPage(0);
  };

  // Load SSPs on mount
  useEffect(() => {
    setPlansLoading(true);
    listSSPs()
      .then((res) => {
        const sspList = (res.data || []).filter(
          (s) => (s.status ?? (s as any).lifecycleStatus) === "published",
        );
        setSsps(sspList);
        const nameMap = new Map<string, string>();
        sspList.forEach((s) => {
          const name = s.title || s.documentId;
          nameMap.set(s.id, name);
          nameMap.set(s.documentId, name);
        });
        setSspMap(nameMap);
      })
      .catch((e: Error) => setPlansError(e.message))
      .finally(() => setPlansLoading(false));
  }, []);

  function resolveSspName(href: string): string {
    if (!href) return "-";
    const last = href.split("/").pop() || "";
    return sspMap.get(last) || sspMap.get(href) || last || "-";
  }

  function handleSspClick(ssp: SspSummaryResponse) {
    setSelectedSsp(ssp);
    setSelectedPlan(null);
    setView("controls");
    setControlsLoading(true);
    setControlsError(null);
    setDriftFilteringLoading(false);
    setDriftFilterStart("");
    setDriftFilterEnd("");
    setReviewedControls([]);
    setDriftControlMap(new Map());
    setDriftSummary(null);
    setSspPlans([]);
    setSelectedPlanFilter(null);

    // Fetch plans for this SSP (for the plan filter dropdown) in the background
    fetchAssessmentPlansBySsp(ssp.id)
      .then((plans) =>
        setSspPlans(
          plans.filter(
            (p) => (p.status ?? p.lifecycleStatus) === "approved",
          ),
        ),
      )
      .catch(() => setSspPlans([]));

    postDriftBySsp(ssp.id)
      .then((res) => {
        const raw = res as any;
        const data = raw?.data ?? raw;
        const m = new Map<string, DriftApiControl>();
        (data.controls ?? []).forEach((c: DriftApiControl) =>
          m.set(c.controlId, c),
        );
        setDriftControlMap(m);
        setDriftSummary((data.summary as DriftSummary | undefined) ?? null);
        // Build reviewed controls from the drift result
        const ctrls: ApiReviewedControl[] = Array.from(m.values()).map((c) => ({
          id: c.controlId,
          controlId: c.controlId,
          controlDescription: c.controlTitle ?? c.controlId,
          statements: [],
          byComponents: [],
        }));
        setReviewedControls(ctrls);
      })
      .catch((e: Error) => setControlsError(e.message))
      .finally(() => setControlsLoading(false));
  }

  function handlePlanClick(plan: ApiAssessmentPlan) {
    setSelectedPlan(plan);
    setView("controls");
    setControlsLoading(true);
    setControlsError(null);
    setDriftFilteringLoading(false);
    setDriftFilterStart("");
    setDriftFilterEnd("");
    setReviewedControls([]);
    setDriftControlMap(new Map());
    setDriftSummary(null);

    const { start, end } = planDates(plan);

    const controlsPromise = getAssessmentPlanReviewedControls(plan.id)
      .then((res) => res.data || [])
      .catch(() =>
        getAssessmentPlan(plan.id)
          .then((res) => res.data.reviewedControls || [])
          .catch(() => [] as ApiReviewedControl[]),
      );

    const driftPromise = postDrift({
      planId: plan.id,
      startDate: toStartIso(start),
      endDate: toEndIso(end),
    })
      .then((res) => {
        const raw = res as any;
        const data = raw?.data ?? raw;
        const m = new Map<string, DriftApiControl>();
        (data.controls ?? []).forEach((c: DriftApiControl) =>
          m.set(c.controlId, c),
        );
        return {
          map: m,
          summary: (data.summary as DriftSummary | undefined) ?? null,
        };
      })
      .catch(() => ({
        map: new Map<string, DriftApiControl>(),
        summary: null as DriftSummary | null,
      }));

    Promise.all([controlsPromise, driftPromise])
      .then(([ctrls, drift]) => {
        setReviewedControls(ctrls);
        setDriftControlMap(drift.map);
        setDriftSummary(drift.summary);
      })
      .catch((e: Error) => setControlsError(e.message))
      .finally(() => setControlsLoading(false));
  }

  function handleDateFilter(start: string, end: string) {
    // Convert yyyy-mm-dd → ISO with time: start = T00:00:00Z, end = T23:59:59Z
    const toStartZ = (d: string) => d ? `${d}T00:00:00Z` : undefined;
    const toEndZ   = (d: string) => d ? `${d}T23:59:59Z` : undefined;

    const dateParams = (start || end)
      ? { startDate: toStartZ(start), endDate: toEndZ(end) }
      : undefined;

    setDriftFilteringLoading(true);
    setControlsError(null);

    // When a plan filter is active on an SSP view, use plan-level drift endpoint
    if (selectedSsp && selectedPlanFilter) {
      const now = new Date();
      const monthAgo = new Date(now);
      monthAgo.setDate(monthAgo.getDate() - 30);
      postDriftByPlan({
        planId: selectedPlanFilter,
        startDate: start ? `${start}T00:00:00Z` : monthAgo.toISOString(),
        endDate: end ? `${end}T23:59:59Z` : now.toISOString(),
      })
        .then((res) => {
          const raw = res as any;
          const data = raw?.data ?? raw;
          const m = new Map<string, DriftApiControl>();
          (data.controls ?? []).forEach((c: DriftApiControl) => m.set(c.controlId, c));
          setDriftControlMap(m);
          setDriftSummary((data.summary as DriftSummary | undefined) ?? null);
        })
        .catch((e: Error) => setControlsError(e.message))
        .finally(() => setDriftFilteringLoading(false));
    } else if (selectedSsp) {
      postDriftBySsp(selectedSsp.id, dateParams)
        .then((res) => {
          const raw = res as any;
          const data = raw?.data ?? raw;
          const m = new Map<string, DriftApiControl>();
          (data.controls ?? []).forEach((c: DriftApiControl) => m.set(c.controlId, c));
          setDriftControlMap(m);
          setDriftSummary((data.summary as DriftSummary | undefined) ?? null);
        })
        .catch((e: Error) => setControlsError(e.message))
        .finally(() => setDriftFilteringLoading(false));
    } else if (selectedPlan) {
      const { start: pStart, end: pEnd } = planDates(selectedPlan);
      postDrift({
        planId: selectedPlan.id,
        startDate: start ? `${start}T00:00:00Z` : toStartIso(pStart),
        endDate: end ? `${end}T23:59:59Z` : toEndIso(pEnd),
      })
        .then((res) => {
          const raw = res as any;
          const data = raw?.data ?? raw;
          const m = new Map<string, DriftApiControl>();
          (data.controls ?? []).forEach((c: DriftApiControl) => m.set(c.controlId, c));
          setDriftControlMap(m);
          setDriftSummary((data.summary as DriftSummary | undefined) ?? null);
        })
        .catch((e: Error) => setControlsError(e.message))
        .finally(() => setDriftFilteringLoading(false));
    } else {
      setDriftFilteringLoading(false);
    }
  }

  function handlePlanFilterChange(planId: string | null) {
    setSelectedPlanFilter(planId);
    setDriftFilteringLoading(true);
    setControlsError(null);
    setDriftFilterStart("");
    setDriftFilterEnd("");

    if (!planId) {
      // Reset to SSP-level drift
      if (selectedSsp) {
        postDriftBySsp(selectedSsp.id)
          .then((res) => {
            const raw = res as any;
            const data = raw?.data ?? raw;
            const m = new Map<string, DriftApiControl>();
            (data.controls ?? []).forEach((c: DriftApiControl) => m.set(c.controlId, c));
            setDriftControlMap(m);
            setDriftSummary((data.summary as DriftSummary | undefined) ?? null);
          })
          .catch((e: Error) => setControlsError(e.message))
          .finally(() => setDriftFilteringLoading(false));
      } else {
        setDriftFilteringLoading(false);
      }
      return;
    }

    // Auto-populate date picker from the plan's own start/end dates
    const plan = sspPlans.find((p) => p.id === planId);
    const { start: planStart, end: planEnd } = plan ? planDates(plan) : { start: "", end: "" };
    if (planStart) setDriftFilterStart(planStart);
    if (planEnd) setDriftFilterEnd(planEnd);

    // Fetch plan-level drift using POST /api/v1/drift/plan
    const startDate = planStart ? toStartIso(planStart) : new Date(Date.now() - 30 * 864e5).toISOString();
    const endDate = planEnd ? toEndIso(planEnd) : new Date().toISOString();
    postDriftByPlan({
      planId,
      startDate,
      endDate,
    })
      .then((res) => {
        const raw = res as any;
        const data = raw?.data ?? raw;
        const m = new Map<string, DriftApiControl>();
        (data.controls ?? []).forEach((c: DriftApiControl) => m.set(c.controlId, c));
        setDriftControlMap(m);
        setDriftSummary((data.summary as DriftSummary | undefined) ?? null);
      })
      .catch((e: Error) => setControlsError(e.message))
      .finally(() => setDriftFilteringLoading(false));
  }

  function handleControlClick(control: ApiReviewedControl) {
    if (!selectedPlan && !selectedSsp) return;

    // Open panel immediately
    setDrilldownOpen(true);
    setDrilldownLoading(false);
    setSelectedReviewedControl(control);

    const driftCtrl = driftControlMap.get(control.controlId) ?? null;
    setSelectedDriftControl(driftCtrl);
  }

  const sspColumns: Column<SspSummaryResponse>[] = [
    {
      key: "#",
      header: "#",
      width: "w-12",
      headerClassName: "!whitespace-nowrap",
      cellClassName: "!whitespace-nowrap min-w-[2.5rem]",
      render: (_, i) => i + 1,
    },
    {
      key: "title",
      header: "SSP Name",
      align: "left",
      sortable: true,
      render: (r) => r.title || r.documentId || "Untitled",
    },
    {
      key: "sourceType",
      header: "Source Type",
      align: "center",
      cellClassName: "whitespace-nowrap",
      sortable: true,
      render: (r) => {
        const t = (r.sourceType || "").toUpperCase();
        if (!t) return <span className="text-muted-foreground">—</span>;
        const cls =
          t === "SERVICE"
            ? "bg-sky-500/20 text-sky-400 border border-sky-500/30"
            : "bg-violet-500/20 text-violet-400 border border-violet-500/30";
        return (
          <span className={`inline-flex w-[92px] justify-center px-2 py-0.5 rounded text-[10px] font-extrabold tracking-widest ${cls}`}>
            {t}
          </span>
        );
      },
    },
    {
      key: "sourceName",
      header: "Source Name",
      align: "left",
      sortable: true,
      render: (r) => r.sourceName || "—",
    },
    {
      key: "profileName",
      header: "Profile",
      align: "left",
      sortable: true,
      render: (r) => r.profileName || "—",
    },
    {
      key: "lifecycleStatus",
      header: "Status",
      sortable: true,
      render: (r) => <PlanStatusBadge status={r.lifecycleStatus} />,
    },
    {
      key: "createdAt",
      header: "Created On",
      sortable: true,
      render: (r) => fmtDate(r.createdAt),
    },
    {
      key: "updatedAt",
      header: "Updated On",
      sortable: true,
      render: (r) => fmtDate(r.updatedAt),
    },
  ];

  const filteredSsps = useMemo(() => {
    if (!planSearch.trim()) return ssps;
    const q = planSearch.toLowerCase();
    return ssps.filter(
      (s) =>
        (s.title || "").toLowerCase().includes(q) ||
        (s.profileName || "").toLowerCase().includes(q) ||
        (s.sourceName || "").toLowerCase().includes(q),
    );
  }, [ssps, planSearch]);

  const sortedSsps = useMemo(() => {
    if (!planSortKey) return filteredSsps;
    const arr = [...filteredSsps];
    arr.sort((a, b) => {
      let aVal: string | number = "";
      let bVal: string | number = "";
      switch (planSortKey) {
        case "title":
          aVal = a.title || a.documentId || "";
          bVal = b.title || b.documentId || "";
          break;
        case "profileName":
          aVal = a.profileName || "";
          bVal = b.profileName || "";
          break;
        case "lifecycleStatus":
          aVal = a.lifecycleStatus || "";
          bVal = b.lifecycleStatus || "";
          break;
        case "sourceType":
          aVal = a.sourceType || "";
          bVal = b.sourceType || "";
          break;
        case "sourceName":
          aVal = a.sourceName || "";
          bVal = b.sourceName || "";
          break;
        case "createdAt":
          aVal = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          bVal = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          break;
        case "updatedAt":
          aVal = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
          bVal = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
          break;
      }
      if (typeof aVal === "number" && typeof bVal === "number") {
        return planSortDir === "asc" ? aVal - bVal : bVal - aVal;
      }
      return planSortDir === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
    return arr;
  }, [filteredSsps, planSortKey, planSortDir]);

  const pagedPlans = useMemo(
    () =>
      sortedSsps.slice(
        planPage * planPageSize,
        planPage * planPageSize + planPageSize,
      ),
    [sortedSsps, planPage, planPageSize],
  );

  return (
    <PageShell className="space-y-6">
      {/* Plans view - page header */}
      {view === "plans" && (
        <div>
          <h1>Drift Management</h1>
          <p className="text-sm d mt-0.5">
            Monitor control drift and compliance trends across assessment plans.
          </p>
        </div>
      )}

      {/* Controls view - breadcrumb */}
      {view === "controls" && (selectedSsp || selectedPlan) && (
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setView("plans"); setSelectedSsp(null); setSelectedPlan(null); setSspPlans([]); setSelectedPlanFilter(null); }}
            className="flex items-center gap-1.5 text-sm d hover:text-foreground transition-colors"
          >
            <ArrowLeft className="size-3.5" />
            Drift Management
          </button>
          <ChevronRight className="size-3.5 d" />
          <span className="text-sm font-medium">
            {selectedSsp?.title || selectedPlan?.title || "Untitled"}
          </span>
        </div>
      )}

      {/* Plans view content */}
      {view === "plans" && (
        <>
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 d/50 pointer-events-none" />
              <input
                type="text"
                placeholder="Search SSPs"
                value={planSearch}
                onChange={(e) => {
                  setPlanSearch(e.target.value);
                  setPlanPage(0);
                }}
                className="w-full pl-9 pr-4 py-2 text-sm bg-card border border-border rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 dark:focus:ring-indigo-500/20 focus:border-indigo-500 dark:focus:border-indigo-500 transition-all placeholder:d/50"
              />
            </div>
            <div className="flex items-center gap-3 ml-auto flex-shrink-0">
              <ViewModeToggle
                value={planViewMode}
                onChange={(mode) => {
                  setPlanViewMode(mode);
                  setPlanPageSize(resolvePageSizeForViewMode(planPageSize, mode));
                  setPlanPage(0);
                }}
              />
              <label className="text-sm d whitespace-nowrap">
                Rows per page
              </label>
              <Dropdown
                value={String(planPageSize)}
                onChange={(v) => {
                  setPlanPageSize(Number(v));
                  setPlanPage(0);
                }}
                options={getPageSizeOptions(planViewMode).map((n) => ({
                  value: String(n),
                  label: String(n),
                }))}
                size="sm"
                align="end"
                className="!min-w-[80px]"
              />
            </div>
          </div>
          <DataTable
            columns={sspColumns}
            data={pagedPlans}
            loading={plansLoading}
            error={plansError}
            rowKey={(r) => r.id}
            onRowClick={handleSspClick}
            rowClassName={() =>
              "cursor-pointer hover:bg-indigo-50/50 dark:hover:bg-white/[0.05]"
            }
            emptyText={
              ssps.length === 0
                ? "No SSPs found."
                : "No SSPs match your search."
            }
            page={planPage}
            pageSize={planPageSize}
            totalItems={filteredSsps.length}
            onPageChange={setPlanPage}
            itemLabel="SSPs"
            hidePageButtons
            sortKey={planSortKey}
            sortDir={planSortDir}
            onSort={handlePlanSort}
            enableCardView
            viewMode={planViewMode}
            onViewModeChange={setPlanViewMode}
            renderCard={(rows) => (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 pt-2 px-1">
                {rows.map((ssp) => {
                  const lc = (ssp.lifecycleStatus || "draft") as Parameters<
                    typeof StatusBadge
                  >[0]["status"];
                  const accent = statusAccent(lc);
                  const title = ssp.title || ssp.documentId || "Untitled";
                  const sourceType = ssp.sourceType;
                  const sourceName = ssp.sourceName;
                  const profileName = ssp.profileName;
                  return (
                    <div
                      key={ssp.id}
                      onClick={() => handleSspClick(ssp)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleSspClick(ssp);
                        }
                      }}
                      className={`group relative flex h-full flex-col rounded-2xl border-2 border-[#e8edf2] dark:border-white/20 bg-indigo-100/40 dark:bg-indigo-500/10 overflow-hidden transition-all duration-300 hover:-translate-y-1 ${accent.hoverBorder} ${accent.hoverGlow} cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40`}
                    >
                      <span
                        aria-hidden
                        className={`pointer-events-none absolute inset-y-0 left-0 w-1/2 -translate-x-full skew-x-12 bg-gradient-to-r from-transparent ${accent.sheenVia} to-transparent group-hover:translate-x-[220%] group-hover:transition-transform group-hover:duration-[900ms] group-hover:ease-out`}
                      />
                      <div className="@container relative z-[1] flex flex-1 flex-col gap-3 p-3.5">
                        <div className="flex items-start justify-between gap-2.5">
                          <div className="flex-1 min-w-0">
                            <TitleTooltip text={title}>
                              <h3 className="text-[13px] font-bold leading-snug tracking-[-0.01em] text-foreground truncate group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                                {title}
                              </h3>
                            </TitleTooltip>
                          </div>
                          <div className="shrink-0">
                            <Globe className="size-4 text-sky-600 dark:text-sky-400" />
                          </div>
                        </div>
                        {sourceType && (
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <TypeBadge
                              label={
                                sourceType.charAt(0).toUpperCase() +
                                sourceType.slice(1).toLowerCase()
                              }
                              variant="indigo"
                            />
                          </div>
                        )}
                        <div className="mt-auto flex flex-col gap-3">
                          <CardStatTiles>
                            <CardStatTile
                              icon={<ShieldCheck className="size-3.5" />}
                              accent="indigo"
                              value={sourceName || sourceType || "—"}
                              label="source"
                              tooltip={sourceName || sourceType || undefined}
                            />
                            <CardStatTile
                              icon={<FileText className="size-3.5" />}
                              accent="emerald"
                              value={profileName || "—"}
                              label="profile"
                              tooltip={profileName || undefined}
                            />
                          </CardStatTiles>
                          <MetaDates
                            created={fmtDate(ssp.createdAt)}
                            updated={fmtDate(ssp.updatedAt)}
                          />
                          <div className="flex items-center justify-between gap-2 pt-3 border-t border-[#eef2f6] dark:border-white/[0.06]">
                            <StatusBadge status={lc} dot />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          />
        </>
      )}

      {/* Controls view */}
      {view === "controls" && (selectedSsp || selectedPlan) && (
        <>
          {controlsError && (
            <div className="px-4 py-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-sm text-red-700 dark:text-red-400">
              {controlsError}
            </div>
          )}
          <ControlsView
            plan={selectedPlan}
            controls={reviewedControls}
            loading={controlsLoading}
            filtering={driftFilteringLoading}
            driftControlMap={driftControlMap}
            summary={driftSummary}
            filterStartDate={driftFilterStart}
            filterEndDate={driftFilterEnd}
            onFilterStartDateChange={setDriftFilterStart}
            onFilterEndDateChange={setDriftFilterEnd}
            onControlClick={handleControlClick}
            onDateFilter={handleDateFilter}
            sspPlans={selectedSsp ? sspPlans : undefined}
            selectedPlanFilter={selectedPlanFilter}
            onPlanFilterChange={handlePlanFilterChange}
          />
        </>
      )}

      {/* Drilldown panel - 70vw, slides in from right */}
      {drilldownOpen && (
        <ControlDrilldownPanel
          control={selectedDriftControl}
          reviewedControl={selectedReviewedControl}
          loading={drilldownLoading}
          onClose={() => {
            setDrilldownOpen(false);
            setSelectedDriftControl(null);
            setSelectedReviewedControl(null);
          }}
        />
      )}
    </PageShell>
  );
}
